import { sql, getTableColumns, type Table } from "drizzle-orm";
import { pgTable, text, varchar, timestamp, integer, boolean, jsonb, index, uniqueIndex, decimal, real, doublePrecision, check } from "drizzle-orm/pg-core";
// Type-only import: drizzle-zod's runtime createInsertSchema produces timestamp
// schemas that crash Zod 3.25+/v4 at parse time (see drizzle-zod-zod325 memory).
// We keep its TYPES for identical inference but build plain z.object() schemas
// ourselves at runtime via zodInsertSchema below.
import type { createInsertSchema as DrizzleZodCreateInsertSchema } from "drizzle-zod";
import { z } from "zod/v4";

/**
 * Zod-v4-safe replacement for drizzle-zod's createInsertSchema.
 * Builds a plain z.object() from the table's columns:
 * - required when the column is NOT NULL and has no default
 * - optional when nullable or defaulted
 * - nullable when the column is nullable
 * Cast to drizzle-zod's type so all inferred Insert* types stay identical.
 */
function buildInsertSchema(table: Table) {
  const shape: Record<string, z.ZodType> = {};
  for (const [name, column] of Object.entries(getTableColumns(table))) {
    let fieldSchema: z.ZodType;
    if (column.enumValues && column.enumValues.length > 0) {
      fieldSchema = z.enum(column.enumValues as [string, ...string[]]);
    } else {
      switch (column.dataType) {
        case "string":
          fieldSchema = z.string();
          break;
        case "number":
          fieldSchema = z.number();
          break;
        case "boolean":
          fieldSchema = z.boolean();
          break;
        case "date":
          fieldSchema = z.date();
          break;
        case "array":
          fieldSchema = z.array(z.unknown());
          break;
        default:
          // jsonb & other unmapped types: accept any JSON value but reject
          // undefined (and null, unless made nullable below) so NOT NULL
          // columns without defaults stay required. z.unknown() would
          // silently accept undefined/null and loosen validation.
          fieldSchema = z.custom<unknown>((v) => v !== undefined && v !== null);
          break;
      }
    }
    if (!column.notNull) fieldSchema = fieldSchema.nullable();
    if (!column.notNull || column.hasDefault) fieldSchema = fieldSchema.optional();
    shape[name] = fieldSchema;
  }
  return z.object(shape);
}

const zodInsertSchema = buildInsertSchema as unknown as typeof DrizzleZodCreateInsertSchema;

// Feature Registry - Centralized definition of all available features
export const FEATURE_REGISTRY = {
  menuItems: [
    'dashboard',
    'goals',
    'tasks',
    'habits',
    'planner',
    'journal',
    'notepad',
    'intentions',
    'gratefuls',
    'checklists',
    'media',
    'drive',
    'community',
    'assistants',
    'calendar',
    'meditation',
    'incantations',
    'analytics',
    'achievements',
    'tours',
    'zeus',
    'vboard',
    'trackerx',
    'health',
    'meal_planner',
    'workout_planner',
    'health_metrics',
    'food_goal',
    'fitness_goal',
    'ai_coach',
    'ask_ai',
    'goal_simulation',
    'ai_create',
    'time_budget',
    'preferences',
    'sops',
    'zeus_settings',
    'vault',
    'billing',
    'api_keys',
    'suggestions',
    'testimonials',
    'contacts',
  ] as const,
  limits: [
    'max_goals',
    'max_tasks',
    'max_habits',
    'max_milestones',
    'max_journal_entries',
    'max_notes',
    'max_checklists',
    'max_media_files',
  ] as const,
  toggles: [
    'ai_features',
    'calendar_sync',
    'google_calendar',
    'custom_branding',
    'api_access',
    'retellOutbound',
  ] as const,
} as const;

// Type helpers for feature registry
export type MenuItem = typeof FEATURE_REGISTRY.menuItems[number];
export type LimitKey = typeof FEATURE_REGISTRY.limits[number];
export type ToggleKey = typeof FEATURE_REGISTRY.toggles[number];

// Plan features structure
export type PlanFeatures = {
  menuAccess: Record<MenuItem, boolean>;
  limits: Record<LimitKey, number>;
  toggles: Record<ToggleKey, boolean>;
};

// Session storage table for authentication
export const sessions = pgTable(
  "sessions",
  {
    sid: varchar("sid").primaryKey(),
    sess: jsonb("sess").notNull(),
    expire: timestamp("expire").notNull(),
  },
  (table) => [index("IDX_session_expire").on(table.expire)],
);

// Tenants table for multi-tenancy
export const tenants = pgTable("tenants", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  slug: text("slug").unique().notNull(), // URL-friendly identifier
  domain: text("domain").unique(), // Custom domain (optional)
  planType: text("plan_type").notNull().default("free"), // 'free', 'pro', 'enterprise'
  currentPlanId: varchar("current_plan_id"), // FK to subscription_plans (nullable for backwards compatibility)
  maxUsers: integer("max_users").notNull().default(1),
  maxGoals: integer("max_goals").notNull().default(10),
  stripeCustomerId: text("stripe_customer_id").unique(),
  stripeSubscriptionId: text("stripe_subscription_id").unique(),
  subscriptionStatus: text("subscription_status").default("inactive"), // 'active', 'inactive', 'canceled', 'past_due'
  trialEndsAt: timestamp("trial_ends_at"),
  isActive: boolean("is_active").notNull().default(true),
  status: text("status").notNull().default("active"), // 'active', 'frozen', 'deleted' - tenant operational status
  welcomeEmailSent: boolean("welcome_email_sent").notNull().default(false),
  lastPaymentDate: timestamp("last_payment_date"),
  lastFailedPaymentDate: timestamp("last_failed_payment_date"),
  deletedAt: timestamp("deleted_at"), // Soft delete timestamp
  deletedBy: varchar("deleted_by"), // User ID who deleted the tenant (no FK to avoid circular reference)
  dunningStage: integer("dunning_stage").notNull().default(0),
  dunningStartedAt: timestamp("dunning_started_at"),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

// Users table for authentication
export const users = pgTable("users", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  email: varchar("email").unique(),
  username: varchar("username").unique(), // For username/password login
  password: text("password"), // Hashed password
  needsPasswordReset: boolean("needs_password_reset").notNull().default(false), // Flag for temporary passwords
  passwordResetToken: text("password_reset_token"), // SHA-256 hash of one-time reset token
  passwordResetTokenExpiresAt: timestamp("password_reset_token_expires_at"), // Expiry for the reset token
  firstName: varchar("first_name"),
  lastName: varchar("last_name"),
  profileImageUrl: varchar("profile_image_url"),
  role: text("role").notNull().default("user"), // 'admin', 'user'
  isMasterAdmin: boolean("is_master_admin").notNull().default(false), // Master admin has full system access
  isActive: boolean("is_active").notNull().default(true),
  emailVerified: boolean("email_verified").notNull().default(false), // True once mailbox ownership has been proven (OAuth or future email-OTP)
  appleUserId: varchar("apple_user_id").unique(), // Legacy — retained to match production schema, not used by application code
  emailVerificationToken: text("email_verification_token"), // Legacy — retained to match production schema, not used by application code
  emailVerificationTokenExpiresAt: timestamp("email_verification_token_expires_at"), // Legacy — retained to match production schema, not used by application code
  lastLoginAt: timestamp("last_login_at"),
  passwordChangedAt: timestamp("password_changed_at"),
  twoFactorSecretEncrypted: text("two_factor_secret_encrypted"),
  twoFactorRecoveryCodeHashes: jsonb("two_factor_recovery_code_hashes").$type<string[]>().notNull().default(sql`'[]'::jsonb`),
  twoFactorEnabledAt: timestamp("two_factor_enabled_at"),
  twoFactorFailedAttempts: integer("two_factor_failed_attempts").notNull().default(0),
  twoFactorLockedUntil: timestamp("two_factor_locked_until"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// User preferences table for storing user-specific settings
export const userPreferences = pgTable("user_preferences", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().unique().references(() => users.id, { onDelete: "cascade" }),
  navigationOrder: jsonb("navigation_order"), // Array of navigation item IDs in preferred order
  taskFilters: jsonb("task_filters"), // Task filter preferences
  goalFilters: jsonb("goal_filters"), // Goal filter preferences
  dismissedHabitIds: jsonb("dismissed_habit_ids"), // Array of habit IDs that user dismissed from completion alerts
  automatedBackupEnabled: boolean("automated_backup_enabled").default(false),
  automatedBackupFrequency: text("automated_backup_frequency"), // 'daily', 'weekly', 'monthly'
  automatedBackupLastRun: timestamp("automated_backup_last_run"),
  automatedBackupDriveFolderId: text("automated_backup_drive_folder_id"), // ID of the 'Backups' folder in Drive
  themeColors: jsonb("theme_colors"), // Custom theme color values { light: {...}, dark: {...} }
  themeFonts: jsonb("theme_fonts"), // Custom font selections { headings: string, body: string }
  journalMetadataCollapsed: boolean("journal_metadata_collapsed").default(false), // Whether journal entry metadata section is collapsed
  notepadMetadataCollapsed: boolean("notepad_metadata_collapsed").default(false), // Whether notepad metadata section is collapsed
  smartSectionExpanded: boolean("smart_section_expanded").default(true), // Whether SMART goal section is expanded on goal detail page
  meditationGoalMinutes: integer("meditation_goal_minutes"), // Daily/weekly/monthly meditation goal in minutes
  meditationGoalPeriod: text("meditation_goal_period"), // 'week', 'month', or 'custom'
  meditationGoalStartDate: timestamp("meditation_goal_start_date"), // Custom period start date
  meditationGoalEndDate: timestamp("meditation_goal_end_date"), // Custom period end date
  hiddenMenuItems: jsonb("hidden_menu_items"), // Array of navigation item IDs to hide from sidebar
  celebrationEnabled: boolean("celebration_enabled").default(true), // Whether habit streak celebrations are shown
  trackerXWidgetConfig: jsonb("tracker_x_widget_config"), // Array of { trackerId, metric } widget cards
  timezone: text("timezone"), // IANA timezone (e.g. "America/Los_Angeles") — the user's local tz, auto-synced by clients; used server-side to compute the user's "today"
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

// User invitations table
export const userInvitations = pgTable("user_invitations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  inviterUserId: varchar("inviter_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  email: varchar("email").notNull(),
  firstName: varchar("first_name"),
  lastName: varchar("last_name"),
  role: text("role").notNull().default("user"), // 'admin', 'user'
  invitationType: text("invitation_type").notNull().default("reseller"), // 'reseller' | 'member' - reseller creates new tenant, member joins existing
  preferredTenantName: varchar("preferred_tenant_name"), // Optional: custom tenant/family name for reseller invitations
  token: varchar("token").notNull().unique(),
  status: text("status").notNull().default("pending"), // 'pending', 'accepted', 'expired', 'cancelled'
  expiresAt: timestamp("expires_at").notNull(),
  acceptedAt: timestamp("accepted_at"),
  migrationStatus: text("migration_status").default("pending"), // 'pending', 'done', 'skipped' - tracks if reseller invitation was migrated to separate tenant
  migratedAt: timestamp("migrated_at"),
  migratedToTenantId: varchar("migrated_to_tenant_id").references(() => tenants.id, { onDelete: "set null" }),
  lastSentAt: timestamp("last_sent_at"),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

export const goals = pgTable("goals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  title: text("title").notNull(),
  description: text("description"),
  whyReason1: text("why_reason_1"),
  whyReason2: text("why_reason_2"),
  whyReason3: text("why_reason_3"),
  whyReason4: text("why_reason_4"),
  whyReason5: text("why_reason_5"),
  category: text("category").notNull(),
  priority: text("priority").notNull().default("medium"),
  status: text("status").notNull().default("active"),
  progress: integer("progress").notNull().default(0),
  progressCalculationType: text("progress_calculation_type").notNull().default("manual"), // 'manual', 'milestones', 'tasks', 'habits', 'custom'
  habitTargetCount: integer("habit_target_count"), // For habit-based progress: how many habit completions = 100%
  customGoalType: text("custom_goal_type"), // 'number', 'currency' - for custom measured goals
  customGoalTitle: text("custom_goal_title"), // e.g., "Pounds Lost", "Sales Calls Made"
  customGoalTarget: doublePrecision("custom_goal_target"), // Target value for custom goals (e.g., 20 for "lose 20 pounds")
  targetValue: text("target_value"),
  currentValue: text("current_value"), // Formatted value (e.g., "$500" or "20")
  currentValueRaw: doublePrecision("current_value_raw"), // Raw numeric value for calculations
  deadline: timestamp("deadline"),
  sortOrder: integer("sort_order").notNull().default(0),
  mediaGalleryExpanded: boolean("media_gallery_expanded").notNull().default(false),
  mediaSlideshowMode: text("media_slideshow_mode").notNull().default("static"),
  mediaSlideshowInterval: integer("media_slideshow_interval").notNull().default(3),
  isFutureGoal: boolean("is_future_goal").notNull().default(false),
  // SMART goal qualification fields
  smartSpecific: boolean("smart_specific").notNull().default(false),
  smartMeasurable: boolean("smart_measurable").notNull().default(false),
  smartAchievable: boolean("smart_achievable").notNull().default(false),
  smartRelevant: boolean("smart_relevant").notNull().default(false),
  smartTimeBound: boolean("smart_time_bound").notNull().default(false),
  smartBypassed: boolean("smart_bypassed").notNull().default(false),
  timeCost: integer("time_cost"), // Estimated time commitment per frequency period (in minutes)
  timeCostFrequency: text("time_cost_frequency"), // 'daily', 'weekly', 'monthly'
  measuredTrackingMode: text("measured_tracking_mode").notNull().default("sum"), // 'sum' (total all entries) or 'trend' (track changes over time)
  showOnMainGoalPage: boolean("show_on_main_goal_page").notNull().default(true), // If false, goal only shows on its source page (e.g. fitness)
  goalSource: text("goal_source"), // null | 'food' | 'fitness' — links goal to health tracking
  healthMetric: text("health_metric"), // null | 'calories'|'protein'|'carbs'|'fat'|'saturatedFat'|'transFat'|'sugar'|'fiber' | 'workouts'|'steps'|'weight'|'bodyFat'|'sleepHours'|'heartRate'
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

export const goalProgressEntries = pgTable("goal_progress_entries", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  goalId: varchar("goal_id").notNull().references(() => goals.id, { onDelete: "cascade" }),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  label: text("label"), // Description/label for the measurement entry
  date: timestamp("date").notNull().default(sql`now()`),
  amount: doublePrecision("amount").notNull(), // Progress amount (e.g., 2 pounds, $500, 5 calls)
  notes: text("notes"),
  displayOrder: integer("display_order").notNull().default(0), // For drag-drop ordering
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

export const goalPlanningItems = pgTable("goal_planning_items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  goalId: varchar("goal_id").notNull().references(() => goals.id, { onDelete: "cascade" }),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  type: text("type").notNull(), // 'who_know', 'what_know', 'what_do'
  content: text("content").notNull(),
  displayOrder: integer("display_order").notNull().default(0),
  convertedToTaskId: varchar("converted_to_task_id").references(() => tasks.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

export const insertGoalPlanningItemSchema = zodInsertSchema(goalPlanningItems).omit({
  id: true,
  createdAt: true,
});

export type InsertGoalPlanningItem = z.infer<typeof insertGoalPlanningItemSchema>;
export type GoalPlanningItem = typeof goalPlanningItems.$inferSelect;

export const journalTemplates = pgTable("journal_templates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  description: text("description"),
  content: jsonb("content").notNull(),
  category: text("category").notNull().default("general"),
  isDefault: boolean("is_default").notNull().default(false),
  createdBy: varchar("created_by").notNull().references(() => users.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

export const journalEntries = pgTable("journal_entries", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  goalId: varchar("goal_id").references(() => goals.id, { onDelete: "cascade" }), // Link to goal (nullable for standalone entries)
  title: text("title").notNull(),
  content: jsonb("content").notNull(), // Rich text content as JSON
  plainContent: text("plain_content").notNull(), // Plain text for search
  category: text("category").notNull().default("Uncategorized"), // Category for organization
  tags: text("tags").array().notNull().default(sql`ARRAY[]::text[]`),
  mood: text("mood"), // Optional mood tracking
  weather: text("weather"), // Optional weather tracking
  isPrivate: boolean("is_private").notNull().default(false),
  aiInsights: jsonb("ai_insights"), // AI-generated insights
  highlights: jsonb("highlights").default(sql`'[]'::jsonb`), // Text highlights with tasks
  status: text("status").notNull().default("published"),
  draftContent: jsonb("draft_content"),
  lastAutoSavedAt: timestamp("last_auto_saved_at"),
  isPinned: boolean("is_pinned").notNull().default(false),
  pinnedAt: timestamp("pinned_at"),
  displayOrder: integer("display_order").notNull().default(0), // For drag-drop ordering
  templateId: varchar("template_id").references(() => journalTemplates.id, { onDelete: "set null" }),
  duplicatedFrom: varchar("duplicated_from").references((): any => journalEntries.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

export const milestones = pgTable("milestones", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  goalId: varchar("goal_id").notNull().references(() => goals.id, { onDelete: "cascade" }),
  title: text("title").notNull(),
  targetValue: text("target_value"),
  dueDate: timestamp("due_date"), // Due date for milestone
  displayOrder: integer("display_order").notNull().default(0), // For drag-drop ordering
  achieved: boolean("achieved").notNull().default(false),
  achievedAt: timestamp("achieved_at"),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

// Kanban columns for task organization
export const kanbanColumns = pgTable("kanban_columns", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  title: text("title").notNull(),
  color: text("color").notNull().default("#6b7280"), // hex color code
  displayOrder: integer("display_order").notNull().default(0),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

// Dashboard widget instances for customizable enterprise dashboard
export const dashboardWidgets = pgTable("dashboard_widgets", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  widgetType: text("widget_type").notNull(), // 'stats-overview', 'top-three-tasks', 'habits-widget', 'active-goals', etc.
  title: text("title"), // Custom widget title (nullable for default)
  size: text("size").notNull().default("full"), // 'full', 'half', 'quarter'
  orderIndex: integer("order_index").notNull().default(0), // For drag-drop ordering
  column: integer("column").notNull().default(0), // Grid column (0, 1, 2 for responsive layout)
  isVisible: boolean("is_visible").notNull().default(true), // Show/hide widget
  settings: jsonb("settings").notNull().default('{}'), // Widget-specific configuration
  page: text("page").notNull().default("dashboard"), // 'dashboard' or 'analytics' - which page this widget belongs to
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

export const tasks = pgTable("tasks", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  goalId: varchar("goal_id").references(() => goals.id, { onDelete: "cascade" }), // Made nullable for standalone tasks
  milestoneId: varchar("milestone_id").references(() => milestones.id, { onDelete: "cascade" }), // Link to milestone
  journalEntryId: varchar("journal_entry_id").references(() => journalEntries.id, { onDelete: "cascade" }), // Link to journal entry
  checklistId: varchar("checklist_id").references((): any => checklists.id, { onDelete: "set null" }),
  columnId: varchar("column_id").references((): any => kanbanColumns.id, { onDelete: "set null" }), // Link to kanban column
  title: text("title").notNull(),
  description: text("description"),
  category: text("category").notNull().default("Uncategorized"), // Task category
  priority: text("priority").notNull().default("medium"), // 'low', 'medium', 'high', 'urgent'
  tags: text("tags").array().notNull().default(sql`'{}'`), // Task tags
  assignedTo: text("assigned_to").notNull().default("Me"), // 'Me' or 'Others'
  assignedToOther: text("assigned_to_other"), // Name of person if assignedTo is 'Others'
  assignedToUserId: varchar("assigned_to_user_id").references(() => users.id, { onDelete: "set null" }), // Link to actual user when assigning to a friend
  completed: boolean("completed").notNull().default(false),
  isTopThree: boolean("is_top_three").notNull().default(false), // Flag for daily top 3 priority tasks
  showOnMainTaskPage: boolean("show_on_main_task_page").notNull().default(true), // If false, task only shows within its goal
  displayOrder: integer("display_order").notNull().default(0), // For manual ordering within groups
  dueDate: timestamp("due_date"),
  isRecurring: boolean("is_recurring").notNull().default(false), // Flag for recurring tasks
  recurrenceFrequency: text("recurrence_frequency"), // 'daily', 'weekly', 'bi-weekly', 'monthly', 'quarterly', 'yearly'
  recurrenceEndDate: timestamp("recurrence_end_date"), // Optional end date for recurrence
  recurrenceCount: integer("recurrence_count"), // Optional number of occurrences
  recurrenceParentId: varchar("recurrence_parent_id"), // Link to parent recurring task (for grouping instances)
  isMasterRecurringTask: boolean("is_master_recurring_task").notNull().default(false), // True for the original/master recurring task
  isRecurringInstance: boolean("is_recurring_instance").notNull().default(false), // True for tasks generated from a master recurring task
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
}, (table) => [
  index("tasks_checklist_id_idx").on(table.checklistId),
]);

export const subTasks = pgTable("sub_tasks", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  taskId: varchar("task_id").notNull().references(() => tasks.id, { onDelete: "cascade" }),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  text: text("text").notNull(),
  completed: boolean("completed").notNull().default(false),
  isStarred: boolean("is_starred").notNull().default(false),
  dueDate: timestamp("due_date"),
  // Milestone-scoped sub-tasks: when set, the sub-task belongs to a specific
  // task milestone (rendered under that milestone, not in the general list).
  milestoneId: varchar("milestone_id").references(() => taskMilestones.id, { onDelete: "cascade" }),
  // One level of nesting: a sub-sub-task points at its parent sub-task.
  parentSubTaskId: varchar("parent_sub_task_id"),
  // Optional dependency on a sibling sub-task (standalone when null).
  dependsOnSubTaskId: varchar("depends_on_sub_task_id"),
  // Assignment, mirroring tasks.assignedTo/assignedToOther/assignedToUserId
  assignedTo: text("assigned_to").notNull().default("Me"),
  assignedToOther: text("assigned_to_other"),
  assignedToUserId: varchar("assigned_to_user_id").references(() => users.id, { onDelete: "set null" }),
  displayOrder: integer("display_order").notNull().default(0),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

export const taskMilestones = pgTable("task_milestones", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  taskId: varchar("task_id").notNull().references(() => tasks.id, { onDelete: "cascade" }),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  title: text("title").notNull(),
  description: text("description"),
  completed: boolean("completed").notNull().default(false),
  completedAt: timestamp("completed_at"),
  dueDate: timestamp("due_date"),
  displayOrder: integer("display_order").notNull().default(0),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

export const insertTaskMilestoneSchema = zodInsertSchema(taskMilestones).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  displayOrder: true,
  completedAt: true,
});
export type TaskMilestone = typeof taskMilestones.$inferSelect;
export type InsertTaskMilestone = z.infer<typeof insertTaskMilestoneSchema>;

export const taskProgressEntries = pgTable("task_progress_entries", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  taskId: varchar("task_id").notNull().references(() => tasks.id, { onDelete: "cascade" }),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  notes: text("notes").notNull(),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

export const activities = pgTable("activities", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  goalId: varchar("goal_id").references(() => goals.id, { onDelete: "cascade" }),
  type: text("type").notNull(), // 'goal_created', 'task_completed', 'milestone_achieved', etc.
  description: text("description").notNull(),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

export const habits = pgTable("habits", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  goalId: varchar("goal_id").references(() => goals.id, { onDelete: "cascade" }), // Link to goal (nullable for standalone habits)
  name: text("name").notNull(),
  description: text("description"),
  category: text("category").notNull(),
  frequency: text("frequency").notNull().default("daily"), // 'daily', 'weekly', 'monthly', 'quarterly'
  target: integer("target").notNull().default(1), // Number of times to complete
  isActive: boolean("is_active").notNull().default(true),
  sortOrder: integer("sort_order").notNull().default(0),
  isHabitStack: boolean("is_habit_stack").notNull().default(false),
  habitStackStartDays: integer("habit_stack_start_days"), // 1, 2, or 3
  habitStackPhase: integer("habit_stack_phase").notNull().default(1), // 1=initial, 2=second sequence, 3=custom continuation
  habitStackCurrentTarget: integer("habit_stack_current_target"), // current phase target
  sequenceSuccessing: boolean("sequence_successing").notNull().default(false),
  habitType: text("habit_type"), // 'positive' | 'negative'
  atomicCue: text("atomic_cue"),
  atomicCraving: text("atomic_craving"),
  atomicResponse: text("atomic_response"),
  atomicReward: text("atomic_reward"),
  atomicChangeCue: text("atomic_change_cue"),
  atomicChangeCraving: text("atomic_change_craving"),
  atomicChangeResponse: text("atomic_change_response"),
  atomicChangeReward: text("atomic_change_reward"),
  isHabitSequencing: boolean("is_habit_sequencing").notNull().default(false),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

export const habitEntries = pgTable("habit_entries", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  habitId: varchar("habit_id").notNull().references(() => habits.id, { onDelete: "cascade" }),
  date: timestamp("date").notNull(),
  completed: boolean("completed").notNull().default(false),
  notes: text("notes"),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

export const habitGoals = pgTable("habit_goals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  habitId: varchar("habit_id").notNull().references(() => habits.id, { onDelete: "cascade" }),
  goalId: varchar("goal_id").notNull().references(() => goals.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

export const habitSequenceSteps = pgTable("habit_sequence_steps", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  habitId: varchar("habit_id").notNull().references(() => habits.id, { onDelete: "cascade" }),
  description: text("description").notNull(),
  sortOrder: integer("sort_order").notNull().default(0),
  frequency: text("frequency").notNull().default("daily"), // 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'quarterly' | 'annually'
  daysOfWeek: jsonb("days_of_week").$type<string[]>(),
  timeOfDay: text("time_of_day"),
  place: text("place"),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

export const journalEntryVersions = pgTable("journal_entry_versions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  entryId: varchar("entry_id").notNull().references(() => journalEntries.id, { onDelete: "cascade" }),
  versionNumber: integer("version_number").notNull(),
  title: text("title").notNull(),
  content: jsonb("content").notNull(),
  tags: text("tags").array().notNull().default(sql`ARRAY[]::text[]`),
  mood: text("mood"),
  weather: text("weather"),
  createdBy: varchar("created_by").notNull().references(() => users.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

// Journal attachments for images, files, and voice notes
export const journalAttachments = pgTable("journal_attachments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  entryId: varchar("entry_id").notNull().references(() => journalEntries.id, { onDelete: "cascade" }),
  type: text("type").notNull(), // 'image', 'file', 'voice'
  name: text("name").notNull(), // filename or voice note title
  url: text("url"), // For object storage URLs
  data: text("data"), // For base64 encoded data
  mimeType: text("mime_type").notNull(),
  size: integer("size").notNull(), // in bytes
  metadata: jsonb("metadata"), // Additional metadata
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

// Calendar Connections for external calendar providers (Google, Outlook, iCal)
export const calendarConnections = pgTable("calendar_connections", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  provider: text("provider").notNull(), // 'google', 'outlook', 'apple', 'ical'
  email: text("email"), // Account email for OAuth providers
  accessToken: text("access_token"), // OAuth access token
  refreshToken: text("refresh_token"), // OAuth refresh token
  tokenExpiresAt: timestamp("token_expires_at"), // Token expiration
  icalUrl: text("ical_url"), // For iCal feeds
  displayName: text("display_name").notNull(), // User-friendly connection name
  isActive: boolean("is_active").notNull().default(true),
  lastSyncAt: timestamp("last_sync_at"),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
}, (t) => [
  // Two concurrent adds of the same iCal feed must not both save: the route's
  // SELECT-then-INSERT pre-check alone is racy, so this partial unique index
  // is the backstop the route's isUniqueViolation() handling relies on.
  uniqueIndex("calendar_connections_tenant_user_ical_url_uniq")
    .on(t.tenantId, t.userId, t.icalUrl)
    .where(sql`provider = 'ical'`),
]);

// External Calendars from connected providers
export const externalCalendars = pgTable("external_calendars", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  connectionId: varchar("connection_id").notNull().references(() => calendarConnections.id, { onDelete: "cascade" }),
  externalCalendarId: text("external_calendar_id").notNull(), // Calendar ID from provider (e.g., Google Calendar ID)
  name: text("name").notNull(), // Calendar name
  description: text("description"),
  color: text("color").default("#3b82f6"),
  isVisible: boolean("is_visible").notNull().default(true), // Toggle visibility in UI
  isPrimary: boolean("is_primary").notNull().default(false), // Is this the user's primary calendar
  lastSyncAt: timestamp("last_sync_at"),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

// GoalVia-owned calendars, distinct from calendars supplied by external providers.
export const internalCalendars = pgTable("internal_calendars", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  color: text("color").notNull().default("#3b82f6"),
  isDefault: boolean("is_default").notNull().default(false),
  isPlanningOnly: boolean("is_planning_only").notNull().default(false),
  isVisible: boolean("is_visible").notNull().default(true),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
}, (t) => [
  index("internal_calendars_tenant_user_order_idx").on(t.tenantId, t.userId, t.sortOrder),
  uniqueIndex("internal_calendars_one_default_per_user_uniq")
    .on(t.tenantId, t.userId)
    .where(sql`is_default = true`),
  check("internal_calendars_default_not_planning_check", sql`NOT (${t.isDefault} AND ${t.isPlanningOnly})`),
]);

export const calendarEvents = pgTable("calendar_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  externalCalendarId: varchar("external_calendar_id").references(() => externalCalendars.id, { onDelete: "cascade" }), // Link to external calendar
  internalCalendarId: varchar("internal_calendar_id").references(() => internalCalendars.id, { onDelete: "set null" }), // GoalVia calendar for local events
  title: text("title").notNull(),
  description: text("description"),
  startTime: timestamp("start_time").notNull(),
  endTime: timestamp("end_time").notNull(),
  allDay: boolean("all_day").notNull().default(false),
  location: text("location"),
  eventType: text("event_type").notNull().default("event"), // 'event', 'goal', 'task', 'reminder'
  category: text("category"), // Optional category for organizing events
  goalId: varchar("goal_id").references(() => goals.id, { onDelete: "cascade" }),
  taskId: varchar("task_id").references(() => tasks.id, { onDelete: "cascade" }),
  habitId: varchar("habit_id").references(() => habits.id, { onDelete: "cascade" }),
  checklistId: varchar("checklist_id").references(() => checklists.id, { onDelete: "cascade" }), // Link to a scheduled checklist
  color: text("color").default("#3b82f6"),
  isRecurring: boolean("is_recurring").notNull().default(false),
  recurrenceRule: text("recurrence_rule"), // RRULE for recurring events
  recurringEventId: text("recurring_event_id"), // Google's master/parent recurring event ID for series instances
  externalEventId: text("external_event_id"), // Event ID from external provider
  googleEventId: text("google_event_id"), // Deprecated - use externalEventId
  isCompleted: boolean("is_completed").notNull().default(false),
  completedAt: timestamp("completed_at"),
  repeatIfIncomplete: boolean("repeat_if_incomplete").notNull().default(false), // auto-forward to next day if task not done
  reminders: jsonb("reminders").default(sql`'[]'::jsonb`), // Array of reminder objects
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

export const intentions = pgTable("intentions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  text: text("text").notNull(),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

export const categories = pgTable("categories", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  color: text("color").notNull().default("#3b82f6"), // Hex color code
  description: text("description"),
  isDefault: boolean("is_default").notNull().default(false), // For system defaults
  sortOrder: integer("sort_order").notNull().default(0),
  scope: text("scope").notNull().default("all"), // "all" = usable everywhere, "goals" = goals only
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
}, (table) => [
  index("categories_tenant_user_idx").on(table.tenantId, table.userId),
]);

// Gratefuls table for gratitude tracking
export const gratefuls = pgTable("gratefuls", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  content: text("content").notNull(),
  displayOrder: integer("display_order").notNull().default(0),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
}, (table) => [
  index("gratefuls_tenant_user_order_idx").on(table.tenantId, table.userId, table.displayOrder),
]);

// Planner items for time-based planning (This Week, This Month, This Quarter, This Year)
export const plannerItems = pgTable("planner_items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  text: text("text").notNull(),
  section: text("section").notNull(), // 'week', 'month', 'quarter', 'year'
  categoryId: varchar("category_id").references(() => categories.id, { onDelete: "set null" }),
  dueDate: timestamp("due_date"),
  status: text("status").notNull().default("active"), // 'active', 'archived'
  displayOrder: integer("display_order").notNull().default(0),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
}, (table) => [
  index("planner_items_tenant_user_section_idx").on(table.tenantId, table.userId, table.section),
]);

// MnM (Media and Meditation) Categories
export const mnmCategories = pgTable("mnm_categories", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  color: text("color").notNull().default("#8b5cf6"), // Purple default for MnM
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
}, (table) => [
  index("mnm_categories_tenant_user_idx").on(table.tenantId, table.userId),
]);

// Meditation tables
export const meditationPlaylists = pgTable("meditation_playlists", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  description: text("description"),
  isActive: boolean("is_active").notNull().default(true),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

export const meditationSongs = pgTable("meditation_songs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  playlistId: varchar("playlist_id").notNull().references(() => meditationPlaylists.id, { onDelete: "cascade" }),
  title: text("title").notNull(),
  artist: text("artist"),
  url: text("url").notNull(), // URL to audio file or streaming source
  duration: integer("duration"), // Duration in seconds
  sortOrder: integer("sort_order").notNull().default(0),
  librarySortOrder: integer("library_sort_order").notNull().default(0), // Sort order in combined library view
  subject: text("subject"), // Subject/category for filtering
  isStarred: boolean("is_starred").notNull().default(false), // Starred/favorite status
  tags: text("tags").array(), // Array of tags for filtering
  mnmCategoryId: varchar("mnm_category_id").references(() => mnmCategories.id, { onDelete: "set null" }), // MnM category for filtering
  contentType: text("content_type").notNull().default("meditation"), // 'meditation' or 'media'
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

export const customMeditations = pgTable("custom_meditations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  scriptText: text("script_text").notNull(), // The meditation script/text
  voiceType: text("voice_type").notNull().default("female"), // 'male', 'female', 'recorded'
  voiceAudioUrl: text("voice_audio_url"), // URL to recorded voice or TTS audio
  backgroundMusicId: varchar("background_music_id").references(() => meditationSongs.id, { onDelete: "set null" }),
  backgroundMusicUrl: text("background_music_url"), // External URL for royalty-free background music
  musicVolume: integer("music_volume").notNull().default(30), // 0-100 percentage
  voiceVolume: integer("voice_volume").notNull().default(70), // 0-100 percentage
  playbackSpeed: integer("playback_speed").notNull().default(5), // 0-10 scale (0=slowest, 5=normal, 10=fastest)
  subject: text("subject"), // Subject/category for filtering
  isStarred: boolean("is_starred").notNull().default(false), // Starred/favorite status
  tags: text("tags").array(), // Array of tags for filtering
  mnmCategoryId: varchar("mnm_category_id").references(() => mnmCategories.id, { onDelete: "set null" }), // MnM category for filtering
  contentType: text("content_type").notNull().default("meditation"), // 'meditation' or 'media'
  isActive: boolean("is_active").notNull().default(true),
  librarySortOrder: integer("library_sort_order").notNull().default(0), // Sort order in combined library view
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

// Meditation sessions for tracking time spent meditating
export const meditationSessions = pgTable("meditation_sessions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  type: text("type").notNull(), // 'playlist', 'single', 'youtube', 'custom'
  sourceId: varchar("source_id"), // ID of the song, playlist, or custom meditation (optional)
  sourceName: text("source_name"), // Name of what was played for display purposes
  contentType: text("content_type").notNull().default("meditation"), // 'meditation' or 'media' - from source item
  startedAt: timestamp("started_at").notNull().default(sql`now()`),
  endedAt: timestamp("ended_at"), // null if session is still active
  durationSeconds: integer("duration_seconds").notNull().default(0), // Total duration in seconds
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

// Playlist shares table for sharing playlists with friends
export const playlistShares = pgTable("playlist_shares", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  playlistId: varchar("playlist_id").notNull().references(() => meditationPlaylists.id, { onDelete: "cascade" }),
  ownerId: varchar("owner_id").notNull().references(() => users.id, { onDelete: "cascade" }), // Playlist owner
  sharedWithId: varchar("shared_with_id").notNull().references(() => users.id, { onDelete: "cascade" }), // Friend it's shared with
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

// Subscription plans table
export const subscriptionPlans = pgTable("subscription_plans", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(), // 'Free', 'Pro', 'Enterprise'
  displayName: text("display_name").notNull(),
  description: text("description"),
  price: integer("price").notNull().default(0), // Price in cents
  currency: text("currency").notNull().default("USD"),
  interval: text("interval").notNull().default("month"), // 'month', 'year' - keeping original name for compatibility
  maxUsers: integer("max_users").notNull().default(1),
  maxGoals: integer("max_goals").notNull().default(10),
  planFeatures: jsonb("plan_features").notNull().default(sql`'{}'::jsonb`), // Structured plan features (menuAccess, limits, toggles)
  storageLimitMb: integer("storage_limit_mb").default(100), // Storage size limit in megabytes
  aiUpcharge: integer("ai_upcharge").default(0), // AI usage upcharge in cents
  mediaStorageUpcharge: integer("media_storage_upcharge").default(0), // Media storage upcharge in cents
  stripeProductId: text("stripe_product_id"),
  stripePriceId: text("stripe_price_id"),
  isActive: boolean("is_active").notNull().default(true),
  isFeatured: boolean("is_featured").notNull().default(false),
  displayOrder: integer("display_order").notNull().default(0),
  landingPagePosition: integer("landing_page_position"), // 1=left, 2=middle, 3=right, null=not shown
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

// Reseller configuration table for white-labeling and customization
export const resellerConfigs = pgTable("reseller_configs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  
  // Landing page customization
  companyName: text("company_name").notNull().default("GoalVia"),
  companyLogo: text("company_logo"), // URL to logo
  heroTitle: text("hero_title").notNull().default("Transform Your Team's Productivity"),
  heroSubtitle: text("hero_subtitle").notNull().default("The comprehensive productivity platform that combines goal tracking, habit formation, journaling, and calendar management to help teams achieve more together."),
  primaryColor: text("primary_color").notNull().default("#3b82f6"),
  secondaryColor: text("secondary_color").notNull().default("#1e293b"),
  
  // Pricing customization
  showPricing: boolean("show_pricing").notNull().default(true),
  customPricingMessage: text("custom_pricing_message"),
  
  // Contact and branding
  supportEmail: text("support_email"),
  website: text("website"),
  termsUrl: text("terms_url"),
  privacyUrl: text("privacy_url"),
  
  // Feature toggles
  enableCustomDomains: boolean("enable_custom_domains").notNull().default(false),
  enableWhiteLabeling: boolean("enable_white_labeling").notNull().default(false),
  
  // Stripe integration
  stripeAccountId: text("stripe_account_id"), // Stripe Connect account ID from dev site
  
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

// Affiliates table for tracking affiliate partners
export const affiliates = pgTable("affiliates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  
  // Affiliate details
  name: text("name").notNull(),
  email: text("email").notNull(),
  referralCode: text("referral_code").notNull(), // Unique code for referral links
  
  // Commission settings
  commissionType: text("commission_type").notNull().default("percentage"), // 'percentage' | 'fixed'
  commissionValue: doublePrecision("commission_value").notNull().default(10), // Percentage (10.5 = 10.5%) or cents for fixed
  
  // Status and tracking
  status: text("status").notNull().default("active"), // 'active' | 'inactive' | 'pending'
  totalReferrals: integer("total_referrals").notNull().default(0),
  totalConversions: integer("total_conversions").notNull().default(0),
  totalEarned: integer("total_earned").notNull().default(0), // In cents
  totalPaid: integer("total_paid").notNull().default(0), // In cents
  
  // Optional user link (if affiliate is also a platform user)
  userId: varchar("user_id").references(() => users.id, { onDelete: "set null" }),
  
  // Contact and notes
  phone: text("phone"),
  notes: text("notes"),
  
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

// Affiliate referrals table for tracking conversions
export const affiliateReferrals = pgTable("affiliate_referrals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  affiliateId: varchar("affiliate_id").notNull().references(() => affiliates.id, { onDelete: "cascade" }),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  
  // Referred user/tenant
  referredEmail: text("referred_email").notNull(),
  referredUserId: varchar("referred_user_id").references(() => users.id, { onDelete: "set null" }),
  referredTenantId: varchar("referred_tenant_id").references(() => tenants.id, { onDelete: "set null" }),
  
  // Conversion tracking
  status: text("status").notNull().default("pending"), // 'pending' | 'converted' | 'paid' | 'cancelled'
  subscriptionPlanId: varchar("subscription_plan_id").references(() => subscriptionPlans.id, { onDelete: "set null" }),
  commissionAmount: integer("commission_amount").notNull().default(0), // In cents
  
  // Dates
  clickedAt: timestamp("clicked_at").notNull().default(sql`now()`),
  convertedAt: timestamp("converted_at"),
  paidAt: timestamp("paid_at"),
  
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

// Affiliate payouts table for tracking payments to affiliates
export const affiliatePayouts = pgTable("affiliate_payouts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  affiliateId: varchar("affiliate_id").notNull().references(() => affiliates.id, { onDelete: "cascade" }),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  
  // Payout details
  amount: integer("amount").notNull(), // In cents
  status: text("status").notNull().default("pending"), // 'pending' | 'processing' | 'completed' | 'failed'
  payoutMethod: text("payout_method").notNull().default("manual"), // 'manual' | 'paypal' | 'bank_transfer' | 'stripe'
  
  // Reference info
  referenceNumber: text("reference_number"),
  notes: text("notes"),
  
  // Dates
  requestedAt: timestamp("requested_at").notNull().default(sql`now()`),
  processedAt: timestamp("processed_at"),
  completedAt: timestamp("completed_at"),
  
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

// Landing page sections for granular page building
export const landingPageSections = pgTable("landing_page_sections", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  
  // Section identification
  sectionType: text("section_type").notNull(), // 'hero', 'features', 'pricing', 'testimonials', 'cta', 'faq', 'footer'
  sectionName: text("section_name").notNull(), // Custom name for management
  sortOrder: integer("sort_order").notNull().default(0),
  isActive: boolean("is_active").notNull().default(true),
  
  // Content configuration
  title: text("title"),
  subtitle: text("subtitle"),
  description: text("description"),
  content: jsonb("content").notNull().default(sql`'{}'::jsonb`), // Flexible content structure
  
  // Styling and layout
  backgroundColor: text("background_color").default("#ffffff"),
  textColor: text("text_color").default("#000000"),
  accentColor: text("accent_color").default("#3b82f6"),
  layout: text("layout").default("default"), // 'default', 'centered', 'split', 'grid'
  padding: text("padding").default("medium"), // 'small', 'medium', 'large'
  
  // Media assets
  backgroundImage: text("background_image"),
  primaryImage: text("primary_image"),
  secondaryImages: jsonb("secondary_images").default(sql`'[]'::jsonb`),
  
  // Interactive elements
  buttons: jsonb("buttons").default(sql`'[]'::jsonb`), // Array of button configs
  links: jsonb("links").default(sql`'[]'::jsonb`), // Array of link configs
  
  // SEO and meta
  seoTitle: text("seo_title"),
  seoDescription: text("seo_description"),
  seoKeywords: text("seo_keywords").array().default(sql`ARRAY[]::text[]`),
  
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

// Feature comparison matrix for detailed pricing tables
export const featureComparisons = pgTable("feature_comparisons", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  
  // Feature details
  featureCategory: text("feature_category").notNull(), // 'Core', 'AI', 'Collaboration', 'Analytics'
  featureName: text("feature_name").notNull(),
  featureDescription: text("feature_description"),
  featureType: text("feature_type").notNull().default("boolean"), // 'boolean', 'limit', 'text', 'badge'
  
  // Plan availability
  freeValue: text("free_value"), // 'true', 'false', '5 goals', 'Basic only', etc.
  proValue: text("pro_value"),
  enterpriseValue: text("enterprise_value"),
  
  // Display settings
  sortOrder: integer("sort_order").notNull().default(0),
  isHighlighted: boolean("is_highlighted").notNull().default(false),
  icon: text("icon"), // Lucide icon name
  tooltipText: text("tooltip_text"),
  
  // AI pricing tier availability
  aiBasicValue: text("ai_basic_value"),
  aiProValue: text("ai_pro_value"),
  aiEnterpriseValue: text("ai_enterprise_value"),
  
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

// Pricing configuration templates and samples
export const pricingTemplates = pgTable("pricing_templates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  
  // Template identification
  templateName: text("template_name").notNull(),
  industry: text("industry").notNull(), // 'productivity', 'project-management', 'crm', 'general'
  companySize: text("company_size").notNull(), // 'startup', 'smb', 'enterprise', 'any'
  
  // Template data
  subscriptionPlans: jsonb("subscription_plans").notNull(),
  aiPricingTiers: jsonb("ai_pricing_tiers").notNull(),
  featureMatrix: jsonb("feature_matrix").notNull(),
  landingSections: jsonb("landing_sections").notNull(),
  
  // Metadata
  description: text("description"),
  isPublic: boolean("is_public").notNull().default(false),
  usageCount: integer("usage_count").notNull().default(0),
  
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

// AI pricing tiers configuration (Replit-style billing model)
export const aiPricingTiers = pgTable("ai_pricing_tiers", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  
  // Tier details
  name: text("name").notNull(), // 'Free', 'Pro', 'Enterprise'
  displayName: text("display_name").notNull(),
  description: text("description"),
  
  // Base monthly pricing (Replit model)
  monthlyMinimum: decimal("monthly_minimum", { precision: 10, scale: 2 }).notNull().default("0.00"), // Monthly minimum charge
  
  // AI token pricing
  tokensIncluded: integer("tokens_included").notNull().default(0), // Free tokens per month
  pricePerThousandTokens: decimal("price_per_thousand_tokens", { precision: 10, scale: 4 }).notNull().default("0.0000"), // Price per 1K tokens
  
  // Usage limits
  maxTokensPerMonth: integer("max_tokens_per_month"), // null = unlimited
  maxRequestsPerMinute: integer("max_requests_per_minute").notNull().default(10),
  maxRequestsPerDay: integer("max_requests_per_day").notNull().default(100),
  
  // Features
  enableJournalAI: boolean("enable_journal_ai").notNull().default(false),
  enableGoalSuggestions: boolean("enable_goal_suggestions").notNull().default(false),
  enableInsights: boolean("enable_insights").notNull().default(false),
  enableAdvancedAnalytics: boolean("enable_advanced_analytics").notNull().default(false),
  
  // Priority and display
  priority: integer("priority").notNull().default(0), // Lower number = higher priority in display
  isActive: boolean("is_active").notNull().default(true),
  isRecommended: boolean("is_recommended").notNull().default(false),
  
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

// AI usage tracking for billing
export const aiUsage = pgTable("ai_usage", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  
  // Usage details
  feature: text("feature").notNull(), // 'journal_insights', 'goal_suggestions', 'analytics', etc.
  model: text("model").notNull(), // 'gpt-5', 'gpt-4o', etc.
  tokensUsed: integer("tokens_used").notNull(),
  
  // Cost calculation
  costPerToken: decimal("cost_per_token", { precision: 10, scale: 6 }).notNull(),
  totalCost: decimal("total_cost", { precision: 10, scale: 4 }).notNull(),
  
  // Request details
  requestId: varchar("request_id"), // For debugging and tracking
  requestDuration: integer("request_duration"), // milliseconds
  
  // Billing period
  billingMonth: text("billing_month").notNull(), // YYYY-MM format
  
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

// Monthly AI billing summaries
export const aiBillingSummaries = pgTable("ai_billing_summaries", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  
  // Billing period
  billingMonth: text("billing_month").notNull(), // YYYY-MM format
  
  // Usage summary
  totalTokensUsed: integer("total_tokens_used").notNull().default(0),
  totalRequests: integer("total_requests").notNull().default(0),
  
  // Cost breakdown
  baseCost: decimal("base_cost", { precision: 10, scale: 2 }).notNull().default("0.00"), // Monthly minimum
  tokenCost: decimal("token_cost", { precision: 10, scale: 2 }).notNull().default("0.00"), // Variable token usage
  totalCost: decimal("total_cost", { precision: 10, scale: 2 }).notNull().default("0.00"), // Max(baseCost, tokenCost)
  
  // Breakdown by feature
  featureBreakdown: jsonb("feature_breakdown").notNull().default(sql`'{}'::jsonb`), // { feature: { tokens, cost } }
  
  // Billing status
  isBilled: boolean("is_billed").notNull().default(false),
  billedAt: timestamp("billed_at"),
  stripeInvoiceId: text("stripe_invoice_id"),
  
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
}, (table) => [
  index("ai_billing_summaries_tenant_month_idx").on(table.tenantId, table.billingMonth),
]);

// Coupons table for discount management
export const coupons = pgTable("coupons", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  
  // Coupon details
  code: text("code").notNull().unique(), // Coupon code users enter
  name: text("name").notNull(), // Internal name/description
  discountType: text("discount_type").notNull(), // 'percentage', 'fixed_amount'
  discountValue: doublePrecision("discount_value").notNull(), // Percentage (e.g. 12.5 = 12.5%) or cents for fixed_amount
  
  // Free trial settings
  freeTrialDays: integer("free_trial_days").default(0), // 0 = no trial, >0 = trial days
  
  // Plan-specific discounts
  applyToMonthly: boolean("apply_to_monthly").notNull().default(true),
  applyToAnnual: boolean("apply_to_annual").notNull().default(true),
  monthlyDiscountOverride: integer("monthly_discount_override"), // Optional different discount for monthly
  annualDiscountOverride: integer("annual_discount_override"), // Optional different discount for annual
  
  // Stripe integration
  stripeCouponId: text("stripe_coupon_id"), // Stripe coupon ID for sync
  
  // Usage limits
  maxRedemptions: integer("max_redemptions"), // null = unlimited
  timesRedeemed: integer("times_redeemed").notNull().default(0),
  
  // Validity period
  validFrom: timestamp("valid_from").notNull().default(sql`now()`),
  validUntil: timestamp("valid_until"), // null = no expiration
  
  // Status
  isActive: boolean("is_active").notNull().default(true),
  
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

// Updates/Announcements for admin to broadcast to users (app-wide, no tenant restriction)
export const updates = pgTable("updates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").references(() => tenants.id, { onDelete: "cascade" }), // Nullable for app-wide announcements
  text: text("text").notNull(), // Plain text content (fallback for backward compatibility)
  contentHtml: text("content_html"), // Rich HTML content from Tiptap editor
  imageUrl: text("image_url"), // Base64-encoded image data (optional)
  createdBy: varchar("created_by").notNull().references(() => users.id, { onDelete: "cascade" }), // Admin who created it
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});


// Insert schemas
export const insertGoalSchema = zodInsertSchema(goals).omit({
  id: true,
  sortOrder: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  deadline: z.preprocess(
    (val) => {
      if (val === null) return null;
      if (typeof val === 'string') {
        const date = new Date(val);
        return isNaN(date.getTime()) ? null : date;
      }
      return val;
    },
    z.date().nullable()
  ),
});

export const insertTaskSchema = zodInsertSchema(tasks).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  displayOrder: true,
}).extend({
  dueDate: z.union([
    z.string().transform((val) => {
      const date = new Date(val);
      return date;
    }).refine((date) => !isNaN(date.getTime()), {
      message: "Invalid date string",
    }),
    z.date(),
  ]).nullable().optional(),
  recurrenceEndDate: z.union([
    z.string().transform((val) => {
      const date = new Date(val);
      return date;
    }).refine((date) => !isNaN(date.getTime()), {
      message: "Invalid recurrence end date string",
    }),
    z.date(),
  ]).nullable().optional(),
});

export const insertSubTaskSchema = zodInsertSchema(subTasks).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  displayOrder: true,
}).extend({
  dueDate: z.union([
    z.string().transform((val) => {
      const date = new Date(val);
      return date;
    }).refine((date) => !isNaN(date.getTime()), {
      message: "Invalid date string",
    }),
    z.date(),
  ]).nullable().optional(),
});

export const insertMilestoneSchema = zodInsertSchema(milestones).omit({
  id: true,
  createdAt: true,
  achievedAt: true,
}).extend({
  dueDate: z.union([
    z.string().transform((val) => {
      const date = new Date(val);
      return date;
    }).refine((date) => !isNaN(date.getTime()), {
      message: "Invalid date string",
    }),
    z.date(),
  ]).nullable().optional(),
});

export const insertActivitySchema = zodInsertSchema(activities).omit({
  id: true,
  createdAt: true,
});

export const insertTaskProgressEntrySchema = zodInsertSchema(taskProgressEntries).omit({
  id: true,
  createdAt: true,
});

export const insertGoalProgressEntrySchema = zodInsertSchema(goalProgressEntries).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  date: z.union([
    z.string().transform((val) => {
      const date = new Date(val);
      return date;
    }).refine((date) => !isNaN(date.getTime()), {
      message: "Invalid date string",
    }),
    z.date(),
  ]).optional(),
});

export const insertHabitSchema = zodInsertSchema(habits).omit({
  id: true,
  createdAt: true,
});

export const insertHabitEntrySchema = zodInsertSchema(habitEntries).omit({
  id: true,
  createdAt: true,
});

export const insertHabitSequenceStepSchema = zodInsertSchema(habitSequenceSteps).omit({
  id: true,
  createdAt: true,
});

export const insertHabitGoalSchema = zodInsertSchema(habitGoals).omit({
  id: true,
  createdAt: true,
});

export const insertJournalEntrySchema = zodInsertSchema(journalEntries).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  lastAutoSavedAt: true,
  pinnedAt: true,
});

export const insertJournalTemplateSchema = zodInsertSchema(journalTemplates).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertJournalEntryVersionSchema = zodInsertSchema(journalEntryVersions).omit({
  id: true,
  createdAt: true,
});

export const insertJournalAttachmentSchema = zodInsertSchema(journalAttachments).omit({
  id: true,
  createdAt: true,
});

export const insertCalendarConnectionSchema = zodInsertSchema(calendarConnections).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertExternalCalendarSchema = zodInsertSchema(externalCalendars).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertInternalCalendarSchema = zodInsertSchema(internalCalendars).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertCalendarEventSchema = zodInsertSchema(calendarEvents).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  completedAt: true,
});

export const insertIntentionSchema = zodInsertSchema(intentions).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  sortOrder: true, // Calculated in createIntention
});

// Schema for updating intentions with validation
export const intentionUpdateSchema = z.object({
  text: z.string().optional(),
  sortOrder: z.number().optional(),
});

export const insertCategorySchema = zodInsertSchema(categories).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Schema for updating categories with validation
export const categoryUpdateSchema = z.object({
  name: z.string().optional(),
  color: z.string().optional(),
  description: z.string().optional(),
  sortOrder: z.number().optional(),
  /** "all" = usable everywhere, "goals" = Goals only, "sops" = SOPs only */
  scope: z.enum(["all", "goals", "sops"]).optional(),
});

export const insertGratefulSchema = zodInsertSchema(gratefuls).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  displayOrder: true, // Calculated in createGrateful
});

export const insertPlannerItemSchema = zodInsertSchema(plannerItems).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// MnM category schemas
export const insertMnmCategorySchema = zodInsertSchema(mnmCategories).omit({
  id: true,
  createdAt: true,
});

// Meditation schemas
export const insertMeditationPlaylistSchema = zodInsertSchema(meditationPlaylists).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertMeditationSongSchema = zodInsertSchema(meditationSongs).omit({
  id: true,
  createdAt: true,
});

export const insertCustomMeditationSchema = zodInsertSchema(customMeditations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertMeditationSessionSchema = zodInsertSchema(meditationSessions).omit({
  id: true,
  createdAt: true,
});

export const insertPlaylistShareSchema = zodInsertSchema(playlistShares).omit({
  id: true,
  createdAt: true,
});

// Add insert schemas for new tables
export const insertTenantSchema = zodInsertSchema(tenants).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertUserSchema = zodInsertSchema(users).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  lastLoginAt: true,
});

// Schema for updating users - only allows safe fields
export const updateUserSchema = z.object({
  email: z.string().email().transform(v => v.toLowerCase()).optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  role: z.enum(["user", "admin"]).optional(),
  // Master-admin flag — only honored by the server when the caller is
  // themselves a master admin (see PATCH /api/users/:id guards).
  isMasterAdmin: z.boolean().optional(),
});

export const insertUserPreferencesSchema = zodInsertSchema(userPreferences).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertUserInvitationSchema = zodInsertSchema(userInvitations).omit({
  id: true,
  token: true,
  expiresAt: true,
  createdAt: true,
  updatedAt: true,
  acceptedAt: true,
});

// Schema for updating user invitations - only allows safe fields
export const updateUserInvitationSchema = z.object({
  email: z.string().email().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  role: z.enum(["user", "admin"]).optional(),
});

// Zod schema for validating plan features structure
const planFeaturesSchema = z.object({
  menuAccess: z.partialRecord(z.enum(FEATURE_REGISTRY.menuItems), z.boolean()).optional().default({}),
  limits: z.partialRecord(z.enum(FEATURE_REGISTRY.limits), z.number().min(0)).optional().default({}),
  toggles: z.partialRecord(z.enum(FEATURE_REGISTRY.toggles), z.boolean()).optional().default({}),
});

export const insertSubscriptionPlanSchema = zodInsertSchema(subscriptionPlans).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  planFeatures: planFeaturesSchema.optional(),
});

export const insertResellerConfigSchema = zodInsertSchema(resellerConfigs).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertAffiliateSchema = zodInsertSchema(affiliates).omit({
  id: true,
  totalReferrals: true,
  totalConversions: true,
  totalEarned: true,
  totalPaid: true,
  createdAt: true,
  updatedAt: true,
}).superRefine((data, ctx) => {
  if (
    data.commissionType === "fixed" &&
    data.commissionValue !== undefined &&
    data.commissionValue !== null &&
    !Number.isInteger(data.commissionValue)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Commission value must be a whole number of cents when type is fixed",
      path: ["commissionValue"],
    });
  }
});

export const insertAffiliateReferralSchema = zodInsertSchema(affiliateReferrals).omit({
  id: true,
  createdAt: true,
});

export const insertAffiliatePayoutSchema = zodInsertSchema(affiliatePayouts).omit({
  id: true,
  createdAt: true,
});

export const updateAffiliateSchema = z.object({
  name: z.string().min(1).optional(),
  email: z.string().email().optional(),
  referralCode: z.string().min(1).optional(),
  commissionType: z.enum(["percentage", "fixed"]).optional(),
  commissionValue: z.number().min(0).optional(),
  status: z.enum(["active", "inactive", "pending"]).optional(),
  phone: z.string().optional(),
  notes: z.string().optional(),
}).superRefine((data, ctx) => {
  if (
    data.commissionType === "fixed" &&
    data.commissionValue !== undefined &&
    !Number.isInteger(data.commissionValue)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Commission value must be a whole number of cents when type is fixed",
      path: ["commissionValue"],
    });
  }
});

export const updateAffiliateReferralSchema = z.object({
  status: z.enum(["pending", "converted", "paid", "cancelled"]).optional(),
  commissionAmount: z.number().int().min(0).optional(),
  convertedAt: z.string().optional(),
  paidAt: z.string().optional(),
});

export const updateAffiliatePayoutSchema = z.object({
  status: z.enum(["pending", "processing", "completed", "failed"]).optional(),
  payoutMethod: z.enum(["manual", "paypal", "bank_transfer", "stripe"]).optional(),
  referenceNumber: z.string().optional(),
  notes: z.string().optional(),
  processedAt: z.string().optional(),
  completedAt: z.string().optional(),
});

export const insertAiPricingTierSchema = zodInsertSchema(aiPricingTiers).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertAiUsageSchema = zodInsertSchema(aiUsage).omit({
  id: true,
  createdAt: true,
});

export const insertAiBillingSummarySchema = zodInsertSchema(aiBillingSummaries).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  billedAt: true,
});

export const insertLandingPageSectionSchema = zodInsertSchema(landingPageSections).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertFeatureComparisonSchema = zodInsertSchema(featureComparisons).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertPricingTemplateSchema = zodInsertSchema(pricingTemplates).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertCouponSchema = zodInsertSchema(coupons).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  timesRedeemed: true,
}).superRefine((data, ctx) => {
  if (
    data.discountType === "fixed_amount" &&
    data.discountValue !== undefined &&
    data.discountValue !== null &&
    !Number.isInteger(data.discountValue)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Discount value must be a whole number of cents when type is fixed_amount",
      path: ["discountValue"],
    });
  }
});

export const updateCouponSchema = z.object({
  code: z.string().min(1).optional(),
  name: z.string().min(1).optional(),
  discountType: z.enum(["percentage", "fixed_amount"]).optional(),
  discountValue: z.number().min(0).optional(),
  freeTrialDays: z.number().int().min(0).optional(),
  applyToMonthly: z.boolean().optional(),
  applyToAnnual: z.boolean().optional(),
  monthlyDiscountOverride: z.number().int().min(0).optional().nullable(),
  annualDiscountOverride: z.number().int().min(0).optional().nullable(),
  stripeCouponId: z.string().optional().nullable(),
  maxRedemptions: z.number().int().min(1).optional().nullable(),
  validFrom: z.any().optional(),
  validUntil: z.any().optional().nullable(),
  isActive: z.boolean().optional(),
}).superRefine((data, ctx) => {
  if (
    data.discountType === "fixed_amount" &&
    data.discountValue !== undefined &&
    !Number.isInteger(data.discountValue)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Discount value must be a whole number of cents when type is fixed_amount",
      path: ["discountValue"],
    });
  }
});

export const insertUpdateSchema = zodInsertSchema(updates).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Frontend-only schemas (exclude backend-populated fields like tenantId and userId)
export const frontendHabitSchema = insertHabitSchema.omit({
  tenantId: true,
  userId: true,
});

export const frontendGoalSchema = insertGoalSchema.omit({
  tenantId: true,
  userId: true,
});

export const frontendCalendarEventSchema = insertCalendarEventSchema.omit({
  tenantId: true,
  userId: true,
});

export const frontendJournalEntrySchema = insertJournalEntrySchema.omit({
  tenantId: true,
  userId: true,
});

export const frontendTaskSchema = insertTaskSchema.omit({
  tenantId: true,
  userId: true,
});

export const frontendMilestoneSchema = insertMilestoneSchema.omit({
  tenantId: true,
  userId: true,
});

export const frontendHabitEntrySchema = insertHabitEntrySchema.omit({
  tenantId: true,
  userId: true,
});

export const frontendActivitySchema = insertActivitySchema.omit({
  tenantId: true,
  userId: true,
});

export const frontendIntentionSchema = insertIntentionSchema.omit({
  tenantId: true,
  userId: true,
});

export const frontendCategorySchema = insertCategorySchema.omit({
  tenantId: true,
  userId: true,
});

export const frontendGratefulSchema = insertGratefulSchema.omit({
  tenantId: true,
  userId: true,
});

// Types
export type Tenant = typeof tenants.$inferSelect;
export type InsertTenant = z.infer<typeof insertTenantSchema>;
export type UpsertUser = typeof users.$inferInsert;
export type User = typeof users.$inferSelect;
export type InsertUser = z.infer<typeof insertUserSchema>;
export type UserPreferences = typeof userPreferences.$inferSelect;
export type InsertUserPreferences = z.infer<typeof insertUserPreferencesSchema>;
export type UserInvitation = typeof userInvitations.$inferSelect;
export type InsertUserInvitation = z.infer<typeof insertUserInvitationSchema>;
export type UpdateUserInvitation = z.infer<typeof updateUserInvitationSchema>;
export type SubscriptionPlan = typeof subscriptionPlans.$inferSelect;
export type InsertSubscriptionPlan = z.infer<typeof insertSubscriptionPlanSchema>;
export type ResellerConfig = typeof resellerConfigs.$inferSelect;
export type InsertResellerConfig = z.infer<typeof insertResellerConfigSchema>;
export type Affiliate = typeof affiliates.$inferSelect;
export type InsertAffiliate = z.infer<typeof insertAffiliateSchema>;
export type AffiliateReferral = typeof affiliateReferrals.$inferSelect;
export type InsertAffiliateReferral = z.infer<typeof insertAffiliateReferralSchema>;
export type AffiliatePayout = typeof affiliatePayouts.$inferSelect;
export type InsertAffiliatePayout = z.infer<typeof insertAffiliatePayoutSchema>;
export type AiPricingTier = typeof aiPricingTiers.$inferSelect;
export type InsertAiPricingTier = z.infer<typeof insertAiPricingTierSchema>;
export type AiUsage = typeof aiUsage.$inferSelect;
export type InsertAiUsage = z.infer<typeof insertAiUsageSchema>;
export type AiBillingSummary = typeof aiBillingSummaries.$inferSelect;
export type InsertAiBillingSummary = z.infer<typeof insertAiBillingSummarySchema>;
export type Goal = typeof goals.$inferSelect;
export type InsertGoal = z.infer<typeof insertGoalSchema>;
export type Task = typeof tasks.$inferSelect;
export type InsertTask = z.infer<typeof insertTaskSchema>;
export type SubTask = typeof subTasks.$inferSelect;
export type InsertSubTask = z.infer<typeof insertSubTaskSchema>;
export type Milestone = typeof milestones.$inferSelect;
export type InsertMilestone = z.infer<typeof insertMilestoneSchema>;
export type Activity = typeof activities.$inferSelect;
export type InsertActivity = z.infer<typeof insertActivitySchema>;
export type GoalProgressEntry = typeof goalProgressEntries.$inferSelect;
export type InsertGoalProgressEntry = z.infer<typeof insertGoalProgressEntrySchema>;
export type TaskProgressEntry = typeof taskProgressEntries.$inferSelect;
export type InsertTaskProgressEntry = z.infer<typeof insertTaskProgressEntrySchema>;
export type Habit = typeof habits.$inferSelect;
export type HabitWithStreakState = Habit & {
  currentStreak?: number;
  checkedInToday?: boolean;
  dueToday?: boolean;
};
export type InsertHabit = z.infer<typeof insertHabitSchema>;
export type HabitEntry = typeof habitEntries.$inferSelect;
export type InsertHabitEntry = z.infer<typeof insertHabitEntrySchema>;
export type HabitGoal = typeof habitGoals.$inferSelect;
export type InsertHabitGoal = z.infer<typeof insertHabitGoalSchema>;
export type HabitSequenceStep = typeof habitSequenceSteps.$inferSelect;
export type InsertHabitSequenceStep = z.infer<typeof insertHabitSequenceStepSchema>;
export type JournalTemplate = typeof journalTemplates.$inferSelect;
export type InsertJournalTemplate = z.infer<typeof insertJournalTemplateSchema>;
export type JournalEntryVersion = typeof journalEntryVersions.$inferSelect;
export type InsertJournalEntryVersion = z.infer<typeof insertJournalEntryVersionSchema>;
export type JournalAttachment = typeof journalAttachments.$inferSelect;
export type InsertJournalAttachment = z.infer<typeof insertJournalAttachmentSchema>;
export type JournalEntry = typeof journalEntries.$inferSelect;
export type InsertJournalEntry = z.infer<typeof insertJournalEntrySchema>;
export type CalendarConnection = typeof calendarConnections.$inferSelect;
export type InsertCalendarConnection = z.infer<typeof insertCalendarConnectionSchema>;
export type ExternalCalendar = typeof externalCalendars.$inferSelect;
export type InsertExternalCalendar = z.infer<typeof insertExternalCalendarSchema>;
export type InternalCalendar = typeof internalCalendars.$inferSelect;
export type InsertInternalCalendar = z.infer<typeof insertInternalCalendarSchema>;
export type CalendarEvent = typeof calendarEvents.$inferSelect;
export type InsertCalendarEvent = z.infer<typeof insertCalendarEventSchema>;
export type Intention = typeof intentions.$inferSelect;
export type InsertIntention = z.infer<typeof insertIntentionSchema>;
export type NewIntention = z.infer<typeof frontendIntentionSchema>;
export type Category = typeof categories.$inferSelect;
export type InsertCategory = z.infer<typeof insertCategorySchema>;
export type NewCategory = z.infer<typeof frontendCategorySchema>;
export type Grateful = typeof gratefuls.$inferSelect;
export type InsertGrateful = z.infer<typeof insertGratefulSchema>;
export type NewGrateful = z.infer<typeof frontendGratefulSchema>;
export type PlannerItem = typeof plannerItems.$inferSelect;
export type InsertPlannerItem = z.infer<typeof insertPlannerItemSchema>;
export type MnmCategory = typeof mnmCategories.$inferSelect;
export type InsertMnmCategory = z.infer<typeof insertMnmCategorySchema>;
export type MeditationPlaylist = typeof meditationPlaylists.$inferSelect;
export type InsertMeditationPlaylist = z.infer<typeof insertMeditationPlaylistSchema>;
export type MeditationSong = typeof meditationSongs.$inferSelect;
export type InsertMeditationSong = z.infer<typeof insertMeditationSongSchema>;
export type CustomMeditation = typeof customMeditations.$inferSelect;
export type InsertCustomMeditation = z.infer<typeof insertCustomMeditationSchema>;
export type MeditationSession = typeof meditationSessions.$inferSelect;
export type InsertMeditationSession = z.infer<typeof insertMeditationSessionSchema>;
export type PlaylistShare = typeof playlistShares.$inferSelect;
export type InsertPlaylistShare = z.infer<typeof insertPlaylistShareSchema>;
export type LandingPageSection = typeof landingPageSections.$inferSelect;
export type InsertLandingPageSection = z.infer<typeof insertLandingPageSectionSchema>;
export type FeatureComparison = typeof featureComparisons.$inferSelect;
export type InsertFeatureComparison = z.infer<typeof insertFeatureComparisonSchema>;
export type PricingTemplate = typeof pricingTemplates.$inferSelect;
export type InsertPricingTemplate = z.infer<typeof insertPricingTemplateSchema>;
export type Coupon = typeof coupons.$inferSelect;
export type InsertCoupon = z.infer<typeof insertCouponSchema>;
export type Update = typeof updates.$inferSelect;
export type InsertUpdate = z.infer<typeof insertUpdateSchema>;

// Extended types for API responses
export type GoalWithDetails = Goal & {
  tasks: Task[];
  milestones: Milestone[];
};

export type TaskProgressEntryWithUser = TaskProgressEntry & {
  user: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    profileImageUrl: string | null;
  };
};

export type HabitWithEntries = Habit & {
  entries: HabitEntry[];
  currentStreak: number;
  longestStreak: number;
  completionRate: number;
};

export type DashboardStats = {
  activeGoals: number;
  completedGoals: number;
  activeTasks: number;
  completedTasks: number;
  successRate: number;
  currentStreak: number;
  activeHabits: number;
  totalHabitsCompleted: number;
  averageHabitStreak: number;
};

export type CategoryStats = {
  [category: string]: number;
};

export type HabitStats = {
  totalHabits: number;
  completedToday: number;
  longestStreak: number;
  completionRate: number;
};

export type JournalEntryWithTasks = JournalEntry & {
  extractedTasks: Array<{
    id: string;
    text: string;
    highlighted: boolean;
    position: { start: number; end: number };
    goalId?: string;
  }>;
};

export type CalendarEventWithDetails = CalendarEvent & {
  goal?: Goal;
  task?: Task;
};

export type JournalStats = {
  totalEntries: number;
  entriesThisMonth: number;
  totalWords: number;
  averageWordsPerEntry: number;
  mostUsedTags: Array<{ tag: string; count: number }>;
  writingStreak: number;
};

export type CalendarStats = {
  totalEvents: number;
  upcomingEvents: number;
  completedEvents: number;
  goalEvents: number;
  taskEvents: number;
};

// Extended types for multi-tenant features
export type TenantWithPlan = Tenant & {
  plan?: SubscriptionPlan;
  userCount: number;
  goalCount: number;
};

export type UserWithTenant = User & {
  tenant: Tenant;
};

// Landing page builder tables
export const landingPages = pgTable("landing_pages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  slug: text("slug").notNull(), // URL identifier
  description: text("description"),
  isActive: boolean("is_active").notNull().default(false),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
}, (t) => [
  // Backstop for pre-check-only creation paths (e.g. the seed-native route's
  // SELECT-then-INSERT slug check): concurrent creates of the same slug must
  // not both save.
  uniqueIndex("landing_pages_tenant_slug_uniq").on(t.tenantId, t.slug),
]);

export const landingPageVersions = pgTable("landing_page_versions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  pageId: varchar("page_id").notNull().references(() => landingPages.id, { onDelete: "cascade" }),
  variantKey: text("variant_key").notNull(), // 'control', 'variant-a', 'variant-b', etc.
  name: text("name").notNull(),
  description: text("description"),
  structure: jsonb("structure").notNull(), // Page element tree structure
  trafficSplit: decimal("traffic_split", { precision: 5, scale: 2 }).notNull().default("0"), // Percentage 0-100
  status: text("status").notNull().default("draft"), // 'draft', 'published', 'archived'
  publishedAt: timestamp("published_at"),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

export const landingPageElements = pgTable("landing_page_elements", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  versionId: varchar("version_id").notNull().references(() => landingPageVersions.id, { onDelete: "cascade" }),
  parentId: varchar("parent_id"),
  type: text("type").notNull(), // 'hero', 'text', 'image', 'button', 'form', 'container', etc.
  props: jsonb("props").notNull().default('{}'), // Element properties and content
  order: integer("order").notNull().default(0), // Order within parent
  breakpointOverrides: jsonb("breakpoint_overrides").default('{}'), // Responsive overrides
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

export const landingPageExperiments = pgTable("landing_page_experiments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  pageId: varchar("page_id").notNull().references(() => landingPages.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  description: text("description"),
  goalMetric: text("goal_metric").notNull().default("conversions"), // 'conversions', 'time_on_page', 'clicks', etc.
  status: text("status").notNull().default("draft"), // 'draft', 'running', 'paused', 'completed'
  startedAt: timestamp("started_at"),
  endedAt: timestamp("ended_at"),
  confidenceLevel: decimal("confidence_level", { precision: 5, scale: 2 }).default("95"), // Statistical confidence
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

export const experimentAssignments = pgTable("experiment_assignments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  experimentId: varchar("experiment_id").notNull().references(() => landingPageExperiments.id, { onDelete: "cascade" }),
  versionId: varchar("version_id").notNull().references(() => landingPageVersions.id, { onDelete: "cascade" }),
  visitorId: text("visitor_id").notNull(), // Anonymous visitor identifier
  sessionId: text("session_id"), // Session identifier
  assignedAt: timestamp("assigned_at").notNull().default(sql`now()`),
});

export const landingPageAnalytics = pgTable("landing_page_analytics", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  versionId: varchar("version_id").notNull().references(() => landingPageVersions.id, { onDelete: "cascade" }),
  date: timestamp("date").notNull(),
  visitorId: text("visitor_id"),
  sessionId: text("session_id"),
  eventType: text("event_type").notNull(), // 'view', 'conversion', 'click', 'scroll', etc.
  elementId: varchar("element_id"), // Which element triggered the event
  value: decimal("value", { precision: 10, scale: 2 }), // Event value (time, money, etc.)
  metadata: jsonb("metadata").default('{}'), // Additional event data
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

// Suggestions table for user feature requests
export const suggestions = pgTable("suggestions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  content: jsonb("content").notNull(), // Rich text content as JSON
  plainContent: text("plain_content").notNull(), // Plain text for search and backwards compatibility
  screenshots: jsonb("screenshots").default(sql`'[]'::jsonb`), // Array of screenshot objects {name: string, data: string (base64)}
  userAlias: varchar("user_alias"), // Optional alias for public display (nullable)
  status: text("status").notNull().default("unread"), // 'unread', 'done', 'deferred', 'archived'
  similarityGroup: varchar("similarity_group"), // AI-generated group ID for similar suggestions
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

// Notes table for notepad feature
export const notes = pgTable("notes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  title: text("title").notNull(),
  content: jsonb("content").notNull(), // Rich text content as JSON
  plainContent: text("plain_content").notNull(), // Plain text for search
  tags: text("tags").array().notNull().default(sql`ARRAY[]::text[]`),
  type: text("type").notNull().default("note"), // 'note' or 'research'
  researchQuestion: text("research_question"), // Original research question if type is 'research'
  isPinned: boolean("is_pinned").notNull().default(false),
  pinnedAt: timestamp("pinned_at"),
  displayOrder: integer("display_order").notNull().default(0), // For drag-drop ordering
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

// Note links table for linking notes to goals, habits, tasks
export const noteLinks = pgTable("note_links", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  noteId: varchar("note_id").notNull().references(() => notes.id, { onDelete: "cascade" }),
  entityType: text("entity_type").notNull(), // 'goal', 'habit', 'task'
  entityId: varchar("entity_id").notNull(), // ID of the linked entity
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

// Note shares table for sharing notes with friends
export const noteShares = pgTable("note_shares", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  noteId: varchar("note_id").notNull().references(() => notes.id, { onDelete: "cascade" }),
  ownerId: varchar("owner_id").notNull().references(() => users.id, { onDelete: "cascade" }), // Note owner
  sharedWithId: varchar("shared_with_id").notNull().references(() => users.id, { onDelete: "cascade" }), // Friend it's shared with
  permission: text("permission").notNull().default("view"), // 'view', 'edit'
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

// Checklist templates for reusable checklists
export const checklistTemplates = pgTable("checklist_templates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  items: jsonb("items").notNull().default(sql`'[]'::jsonb`), // Array of {text: string, displayOrder: number}
  category: text("category"), // Optional category name (matches categories table names)
  sharedWithManus: boolean("shared_with_manus").notNull().default(false), // Visible to Manus via MCP tools
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

// Checklists for active and completed checklist instances
export const checklists = pgTable("checklists", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  templateId: varchar("template_id").references(() => checklistTemplates.id, { onDelete: "set null" }),
  items: jsonb("items").notNull().default(sql`'[]'::jsonb`), // Array of {text: string, checked: boolean, displayOrder: number}
  category: text("category"), // Optional category name (matches categories table names)
  sharedWithManus: boolean("shared_with_manus").notNull().default(false), // Visible to Manus via MCP tools
  instanceDate: text("instance_date"), // 'YYYY-MM-DD' for daily instances created from a template
  isCompleted: boolean("is_completed").notNull().default(false),
  isExpanded: boolean("is_expanded").notNull().default(true), // Persistent collapsed/expanded state
  completedAt: timestamp("completed_at"),
  completedBy: varchar("completed_by").references(() => users.id, { onDelete: "set null" }), // Who completed it
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

// History log of checklist runs: one row per activation of a template (or
// daily-cycle reset of a recurring checklist), stamped when started and when
// completed. Survives checklist deletion and daily resets.
export const checklistRuns = pgTable("checklist_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }), // Checklist owner
  templateId: varchar("template_id").references(() => checklistTemplates.id, { onDelete: "cascade" }),
  checklistId: varchar("checklist_id").references(() => checklists.id, { onDelete: "set null" }),
  checklistName: text("checklist_name").notNull(),
  startedAt: timestamp("started_at").notNull().default(sql`now()`),
  completedAt: timestamp("completed_at"),
  completedBy: varchar("completed_by").references(() => users.id, { onDelete: "set null" }),
});

// Sharing checklists with assistant users (human assistants)
export const checklistShares = pgTable("checklist_shares", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  checklistId: varchar("checklist_id").notNull().references(() => checklists.id, { onDelete: "cascade" }),
  ownerId: varchar("owner_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  sharedWithId: varchar("shared_with_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  permission: text("permission").notNull().default("edit"), // 'view', 'edit'
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
}, (t) => [
  uniqueIndex("checklist_shares_checklist_shared_with_uniq").on(t.checklistId, t.sharedWithId),
]);

// Sharing checklist templates with assistant users (human assistants)
export const checklistTemplateShares = pgTable("checklist_template_shares", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  templateId: varchar("template_id").notNull().references(() => checklistTemplates.id, { onDelete: "cascade" }),
  ownerId: varchar("owner_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  sharedWithId: varchar("shared_with_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  permission: text("permission").notNull().default("edit"), // 'view', 'edit'
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
}, (t) => [
  uniqueIndex("checklist_template_shares_template_shared_with_uniq").on(t.templateId, t.sharedWithId),
]);

// Media files for uploads (images, PDFs, videos)
export const mediaFiles = pgTable("media_files", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  goalId: varchar("goal_id").references(() => goals.id, { onDelete: "set null" }), // Optional link to goal
  name: text("name").notNull(), // User-provided name
  fileName: text("file_name").notNull(), // Original file name
  fileUrl: text("file_url").notNull(), // Storage URL or base64 data
  fileType: text("file_type").notNull(), // 'jpeg', 'png', 'pdf', 'mpeg'
  fileSize: integer("file_size").notNull(), // Size in bytes
  category: text("category").notNull().default("Uncategorized"), // User-provided category
  tags: text("tags").array().notNull().default(sql`ARRAY[]::text[]`), // User-provided tags
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

// Goal media junction table for carousel display on goal pages
export const goalMedia = pgTable("goal_media", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  goalId: varchar("goal_id").notNull().references(() => goals.id, { onDelete: "cascade" }),
  mediaId: varchar("media_id").notNull().references(() => mediaFiles.id, { onDelete: "cascade" }),
  displayOrder: integer("display_order").notNull().default(0), // For drag-drop reordering
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

// Task media junction table for document attachments
export const taskMedia = pgTable("task_media", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  taskId: varchar("task_id").notNull().references(() => tasks.id, { onDelete: "cascade" }),
  mediaId: varchar("media_id").notNull().references(() => mediaFiles.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

// Media file sharing within tenant
export const mediaShares = pgTable("media_shares", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  mediaId: varchar("media_id").notNull().references(() => mediaFiles.id, { onDelete: "cascade" }),
  ownerId: varchar("owner_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  sharedWithId: varchar("shared_with_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  isAssignmentShare: boolean("is_assignment_share").notNull().default(false),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

// Task drive files junction table for document attachments
export const taskDriveFiles = pgTable("task_drive_files", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  taskId: varchar("task_id").notNull().references(() => tasks.id, { onDelete: "cascade" }),
  driveFileId: varchar("drive_file_id").notNull().references(() => driveFiles.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

// Note media junction table for document attachments
export const noteMedia = pgTable("note_media", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  noteId: varchar("note_id").notNull().references(() => notes.id, { onDelete: "cascade" }),
  mediaId: varchar("media_id").notNull().references(() => mediaFiles.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

// Note drive files junction table for document attachments
export const noteDriveFiles = pgTable("note_drive_files", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  noteId: varchar("note_id").notNull().references(() => notes.id, { onDelete: "cascade" }),
  driveFileId: varchar("drive_file_id").notNull().references(() => driveFiles.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

// Calendar event media junction table for document attachments
export const calendarEventMedia = pgTable("calendar_event_media", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  calendarEventId: varchar("calendar_event_id").notNull().references(() => calendarEvents.id, { onDelete: "cascade" }),
  mediaId: varchar("media_id").notNull().references(() => mediaFiles.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

// Calendar event drive files junction table for document attachments
export const calendarEventDriveFiles = pgTable("calendar_event_drive_files", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  calendarEventId: varchar("calendar_event_id").notNull().references(() => calendarEvents.id, { onDelete: "cascade" }),
  driveFileId: varchar("drive_file_id").notNull().references(() => driveFiles.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

// Task URL attachments for external document links
export const taskUrlAttachments = pgTable("task_url_attachments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  taskId: varchar("task_id").notNull().references(() => tasks.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  url: text("url").notNull(),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

// Note URL attachments for external document links
export const noteUrlAttachments = pgTable("note_url_attachments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  noteId: varchar("note_id").notNull().references(() => notes.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  url: text("url").notNull(),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

// Calendar event URL attachments for external document links
export const calendarEventUrlAttachments = pgTable("calendar_event_url_attachments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  calendarEventId: varchar("calendar_event_id").notNull().references(() => calendarEvents.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  url: text("url").notNull(),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

// Goal URL attachments for external document links
export const goalUrlAttachments = pgTable("goal_url_attachments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  goalId: varchar("goal_id").notNull().references(() => goals.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  url: text("url").notNull(),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

// Canonical item-scoped grants. Keep this mirror aligned with @workspace/db.
export const resourceGrants = pgTable("resource_grants", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  ownerTenantId: varchar("owner_tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  ownerUserId: varchar("owner_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  recipientUserId: varchar("recipient_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  resourceType: text("resource_type").notNull(),
  resourceId: varchar("resource_id").notNull(),
  permission: text("permission").notNull().default("view"),
  revokedAt: timestamp("revoked_at"),
  revokedByUserId: varchar("revoked_by_user_id").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
}, (t) => [
  uniqueIndex("resource_grants_live_item_recipient_uniq").on(t.ownerUserId, t.recipientUserId, t.resourceType, t.resourceId).where(sql`${t.revokedAt} IS NULL`),
  index("resource_grants_recipient_idx").on(t.recipientUserId, t.resourceType, t.resourceId),
]);

export const deletionRequests = pgTable("deletion_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  grantId: varchar("grant_id").notNull().references(() => resourceGrants.id, { onDelete: "restrict" }),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  ownerTenantId: varchar("owner_tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  resourceType: text("resource_type").notNull(),
  resourceId: varchar("resource_id").notNull(),
  requestedByUserId: varchar("requested_by_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  status: text("status").notNull().default("pending"),
  decisionByUserId: varchar("decision_by_user_id").references(() => users.id, { onDelete: "set null" }),
  reason: text("reason"),
  decidedAt: timestamp("decided_at"),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
}, (t) => [
  index("deletion_requests_owner_status_idx").on(t.ownerTenantId, t.status),
  uniqueIndex("deletion_requests_one_pending_item_idx").on(t.grantId, t.resourceId).where(sql`${t.status} = 'pending'`),
]);

export const resourceShareEvents = pgTable("resource_share_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  ownerTenantId: varchar("owner_tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  grantId: varchar("grant_id").references(() => resourceGrants.id, { onDelete: "set null" }),
  deletionRequestId: varchar("deletion_request_id").references(() => deletionRequests.id, { onDelete: "set null" }),
  resourceType: text("resource_type").notNull(),
  resourceId: varchar("resource_id").notNull(),
  actorUserId: varchar("actor_user_id").references(() => users.id, { onDelete: "set null" }),
  eventType: text("event_type").notNull(),
  payload: jsonb("payload").notNull().default(sql`'{}'::jsonb`),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
}, (t) => [index("resource_share_events_resource_idx").on(t.ownerTenantId, t.resourceType, t.resourceId)]);

// Community Features - Friendships
export const friendships = pgTable("friendships", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }), // The user who has this friend
  friendId: varchar("friend_id").notNull().references(() => users.id, { onDelete: "cascade" }), // The friend
  friendTenantId: varchar("friend_tenant_id").references(() => tenants.id, { onDelete: "set null" }),
  status: text("status").notNull().default("active"), // 'active', 'blocked'
  displayName: text("display_name"), // User-defined display name for this friend
  alias: text("alias"), // Short nickname/alias for this friend
  isHidden: boolean("is_hidden").notNull().default(false), // Soft-hide for compliance (preserves data)
  hiddenAt: timestamp("hidden_at"), // When the friend was hidden
  hiddenBy: varchar("hidden_by").references(() => users.id, { onDelete: "set null" }), // Who hid the friend (for audit)
  hiddenReason: text("hidden_reason"), // Optional reason for hiding (audit trail)
  permissions: jsonb("permissions").notNull().default('{"notes":false,"goals":false,"calendar":false,"tasks":false,"notepad":false,"habits":false,"trackerX":false,"contacts":false,"ai":false,"mcp":false,"retellInbound":false,"retellOutbound":false,"vault":false}'),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
}, (table) => [
  uniqueIndex("friendships_tenant_user_friend_idx").on(table.tenantId, table.userId, table.friendId),
]);

export const relationshipSpendingBudgets = pgTable("relationship_spending_budgets", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  friendshipId: varchar("friendship_id").notNull().references(() => friendships.id, { onDelete: "cascade" }),
  dailyLimitCents: integer("daily_limit_cents").notNull().default(0),
  timezone: text("timezone").notNull().default("UTC"),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
}, (table) => [
  uniqueIndex("relationship_spending_budgets_friendship_idx").on(table.tenantId, table.friendshipId),
]);

export const relationshipSpendingDailyUsage = pgTable("relationship_spending_daily_usage", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  friendshipId: varchar("friendship_id").notNull().references(() => friendships.id, { onDelete: "cascade" }),
  usageDate: text("usage_date").notNull(),
  reservedCents: integer("reserved_cents").notNull().default(0),
  spentCents: integer("spent_cents").notNull().default(0),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
}, (table) => [
  uniqueIndex("relationship_spending_daily_usage_unique_idx").on(table.tenantId, table.friendshipId, table.usageDate),
]);

export const relationshipSpendingReservations = pgTable("relationship_spending_reservations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  friendshipId: varchar("friendship_id").notNull().references(() => friendships.id, { onDelete: "cascade" }),
  dailyUsageId: varchar("daily_usage_id").notNull().references(() => relationshipSpendingDailyUsage.id, { onDelete: "cascade" }),
  requestId: varchar("request_id").notNull().unique(),
  reservedCents: integer("reserved_cents").notNull(),
  spentCents: integer("spent_cents").notNull().default(0),
  status: text("status").notNull().default("pending"),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  settledAt: timestamp("settled_at"),
});

export const relationshipSpendingApprovals = pgTable("relationship_spending_approvals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  friendshipId: varchar("friendship_id").notNull().references(() => friendships.id, { onDelete: "cascade" }),
  requesterUserId: varchar("requester_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  approverUserId: varchar("approver_user_id").references(() => users.id, { onDelete: "set null" }),
  usageDate: text("usage_date").notNull(),
  requestedCents: integer("requested_cents").notNull(),
  approvedCents: integer("approved_cents").notNull().default(0),
  status: text("status").notNull().default("pending"),
  expiresAt: timestamp("expires_at").notNull(),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  resolvedAt: timestamp("resolved_at"),
}, (table) => [
  uniqueIndex("relationship_spending_approvals_day_idx").on(table.tenantId, table.friendshipId, table.usageDate),
]);

export const friendInvitations = pgTable("friend_invitations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  inviterId: varchar("inviter_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  inviteeEmail: text("invitee_email").notNull(), // Email of person being invited
  inviteeUserId: varchar("invitee_user_id").references(() => users.id, { onDelete: "cascade" }), // Set if invitee is existing user
  inviteeUsername: text("invitee_username"), // Optional username if provided
  displayName: text("display_name"), // User-defined display name for the invitee
  alias: text("alias"), // Short nickname/alias for the invitee
  status: text("status").notNull().default("pending"), // 'pending', 'accepted', 'rejected', 'cancelled'
  token: text("token").notNull(), // Unique token for email link
  message: text("message"), // Optional personal message
  permissions: jsonb("permissions").notNull().default('{"notes":false,"goals":false,"calendar":false,"tasks":false,"notepad":false,"habits":false,"trackerX":false,"contacts":false,"ai":false,"mcp":false,"retellInbound":false,"retellOutbound":false,"vault":false}'),
  dailyBudgetCents: integer("daily_budget_cents"),
  expiresAt: timestamp("expires_at").notNull(), // Token expiry — legacy NULL rows were backfilled; always set for new invitations
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  respondedAt: timestamp("responded_at"),
});

// Friend Groups - for organizing friends into categories (Family, Work, Close Friends, etc.)
export const friendGroups = pgTable("friend_groups", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }), // Owner of this group
  name: text("name").notNull(), // Group name: "Family", "Work", etc.
  color: text("color"), // Optional color for visual distinction
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

// Friend Group Members - linking friends to groups (many-to-many)
export const friendGroupMembers = pgTable("friend_group_members", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  friendGroupId: varchar("friend_group_id").notNull().references(() => friendGroups.id, { onDelete: "cascade" }),
  friendshipId: varchar("friendship_id").notNull().references(() => friendships.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

// Sharing Goals with Friends
export const goalShares = pgTable("goal_shares", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  goalId: varchar("goal_id").notNull().references(() => goals.id, { onDelete: "cascade" }),
  ownerId: varchar("owner_id").notNull().references(() => users.id, { onDelete: "cascade" }), // Goal owner
  sharedWithId: varchar("shared_with_id").notNull().references(() => users.id, { onDelete: "cascade" }), // Friend it's shared with
  permission: text("permission").notNull().default("view"), // 'view', 'edit'
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

// Sharing Tasks with Friends
export const taskShares = pgTable("task_shares", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  taskId: varchar("task_id").notNull().references(() => tasks.id, { onDelete: "cascade" }),
  ownerId: varchar("owner_id").notNull().references(() => users.id, { onDelete: "cascade" }), // Task owner
  sharedWithId: varchar("shared_with_id").notNull().references(() => users.id, { onDelete: "cascade" }), // Friend it's shared with
  permission: text("permission").notNull().default("view"), // 'view', 'edit'
  // Assignment grants are revoked when the task moves to another assignee.
  // User-created shares must survive that reassignment.
  isAssignmentShare: boolean("is_assignment_share").notNull().default(false),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
}, (t) => [
  uniqueIndex("task_shares_assignment_grant_uniq")
    .on(t.tenantId, t.taskId, t.ownerId, t.sharedWithId)
    .where(sql`${t.isAssignmentShare} = true`),
  uniqueIndex("task_shares_explicit_grant_uniq")
    .on(t.tenantId, t.taskId, t.ownerId, t.sharedWithId)
    .where(sql`${t.isAssignmentShare} = false`),
]);

// Messaging System
export const conversations = pgTable("conversations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  type: text("type").notNull().default("direct"), // 'direct' for 1:1, 'group' for future
  directUserLow: varchar("direct_user_low").references(() => users.id, { onDelete: "cascade" }),
  directUserHigh: varchar("direct_user_high").references(() => users.id, { onDelete: "cascade" }),
  lastMessageAt: timestamp("last_message_at").notNull().default(sql`now()`),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

export const conversationParticipants = pgTable("conversation_participants", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  conversationId: varchar("conversation_id").notNull().references(() => conversations.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  lastReadAt: timestamp("last_read_at"),
  joinedAt: timestamp("joined_at").notNull().default(sql`now()`),
  isHidden: boolean("is_hidden").notNull().default(false), // Soft-hide conversation for this user
  hiddenAt: timestamp("hidden_at"), // When hidden
  hiddenBy: varchar("hidden_by").references(() => users.id, { onDelete: "set null" }), // Who hid it (for audit)
});

export const messages = pgTable("messages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  conversationId: varchar("conversation_id").notNull().references(() => conversations.id, { onDelete: "cascade" }),
  senderId: varchar("sender_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  content: text("content").notNull(),
  isRead: boolean("is_read").notNull().default(false),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

// Google Drive Connections
export const driveConnections = pgTable("drive_connections", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  accessToken: text("access_token").notNull(),
  refreshToken: text("refresh_token").notNull(),
  tokenExpiry: timestamp("token_expiry").notNull(),
  email: text("email"),
  appFolderId: text("app_folder_id"),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

// YouTube Connections for OAuth
export const youtubeConnections = pgTable("youtube_connections", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  accessToken: text("access_token").notNull(),
  refreshToken: text("refresh_token").notNull(),
  tokenExpiry: timestamp("token_expiry").notNull(),
  email: text("email"),
  channelId: text("channel_id"), // User's YouTube channel ID
  channelName: text("channel_name"), // User's YouTube channel name
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

// Google Drive Files
export const driveFiles = pgTable("drive_files", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  driveFileId: text("drive_file_id").notNull(),
  name: text("name").notNull(),
  mimeType: text("mime_type").notNull(),
  size: text("size"),
  webViewLink: text("web_view_link"),
  thumbnailLink: text("thumbnail_link"),
  modifiedTime: timestamp("modified_time"),
  createdTime: timestamp("created_time"),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

// Media file shares table for sharing media files with friends
export const mediaFileShares = pgTable("media_file_shares", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  mediaFileId: varchar("media_file_id").notNull().references(() => mediaFiles.id, { onDelete: "cascade" }),
  ownerId: varchar("owner_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  sharedWithId: varchar("shared_with_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  permission: text("permission").notNull().default("view"), // 'view', 'download'
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

// Drive file shares table for sharing Google Drive files with friends
export const driveFileShares = pgTable("drive_file_shares", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  driveFileId: varchar("drive_file_id").notNull().references(() => driveFiles.id, { onDelete: "cascade" }),
  ownerId: varchar("owner_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  sharedWithId: varchar("shared_with_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  isAssignmentShare: boolean("is_assignment_share").notNull().default(false),
  permission: text("permission").notNull().default("view"), // 'view', 'download'
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

// Type definitions for landing page builder
export type LandingPage = typeof landingPages.$inferSelect;
export type LandingPageVersion = typeof landingPageVersions.$inferSelect;
export type LandingPageElement = typeof landingPageElements.$inferSelect;
export type LandingPageExperiment = typeof landingPageExperiments.$inferSelect;
export type ExperimentAssignment = typeof experimentAssignments.$inferSelect;
export type LandingPageAnalytic = typeof landingPageAnalytics.$inferSelect;

// Type definitions for suggestions
export type Suggestion = typeof suggestions.$inferSelect;

// Type definitions for notes
export type Note = typeof notes.$inferSelect;
export type NoteLink = typeof noteLinks.$inferSelect;

// Type definitions for kanban
export type KanbanColumn = typeof kanbanColumns.$inferSelect;

// Type definitions for checklists
export type ChecklistTemplate = typeof checklistTemplates.$inferSelect;
export type Checklist = typeof checklists.$inferSelect;
export type ChecklistRun = typeof checklistRuns.$inferSelect;
export type ChecklistShare = typeof checklistShares.$inferSelect;
export type ChecklistTemplateShare = typeof checklistTemplateShares.$inferSelect;

// Type definitions for community features
export type Friendship = typeof friendships.$inferSelect;
export type RelationshipSpendingBudget = typeof relationshipSpendingBudgets.$inferSelect;
export type RelationshipSpendingDailyUsage = typeof relationshipSpendingDailyUsage.$inferSelect;
export type RelationshipSpendingReservation = typeof relationshipSpendingReservations.$inferSelect;
export type RelationshipSpendingApproval = typeof relationshipSpendingApprovals.$inferSelect;
export type FriendInvitation = typeof friendInvitations.$inferSelect;
export type FriendGroup = typeof friendGroups.$inferSelect;
export type FriendGroupMember = typeof friendGroupMembers.$inferSelect;
export type GoalShare = typeof goalShares.$inferSelect;
export type TaskShare = typeof taskShares.$inferSelect;
export type NoteShare = typeof noteShares.$inferSelect;
export type MediaShare = typeof mediaShares.$inferSelect;
export type Conversation = typeof conversations.$inferSelect;
export type ConversationParticipant = typeof conversationParticipants.$inferSelect;
export type Message = typeof messages.$inferSelect;

// Helper types for community features
export interface FriendWithUser extends Friendship {
  friend: {
    id: string;
    email: string | null;
    username: string | null;
    firstName: string | null;
    lastName: string | null;
    profileImageUrl: string | null;
  };
}

export interface GoalWithOwner extends Goal {
  owner: {
    id: string;
    email: string | null;
    username: string | null;
    firstName: string | null;
    lastName: string | null;
    profileImageUrl: string | null;
  };
}

export interface TaskWithOwner extends Task {
  owner: {
    id: string;
    email: string | null;
    username: string | null;
    firstName: string | null;
    lastName: string | null;
    profileImageUrl: string | null;
  };
}

export interface NoteShareWithOwner {
  id: string;
  noteId: string;
  ownerId: string;
  sharedWithId: string;
  permission: string;
  createdAt: Date;
  note: {
    id: string;
    title: string;
    plainContent: string;
    tags: string[];
    type: string;
  };
  owner: {
    id: string;
    email: string | null;
    username: string | null;
    firstName: string | null;
    lastName: string | null;
    profileImageUrl: string | null;
  };
}

export interface MediaShareWithOwner {
  share: MediaShare;
  media: MediaFile;
  owner: {
    id: string;
    email: string | null;
    username: string | null;
    firstName: string | null;
    lastName: string | null;
    profileImageUrl: string | null;
  };
}

export interface DriveFileShareWithOwner {
  share: DriveFileShare;
  driveFile: DriveFile;
  owner: {
    id: string;
    email: string | null;
    username: string | null;
    firstName: string | null;
    lastName: string | null;
    profileImageUrl: string | null;
  };
}

export interface ConversationWithDetails extends Conversation {
  participants: Array<{
    id: string;
    email: string | null;
    username: string | null;
    firstName: string | null;
    lastName: string | null;
    profileImageUrl: string | null;
  }>;
  otherParticipant?: {
    id: string;
    email: string | null;
    username: string | null;
    firstName: string | null;
    lastName: string | null;
    profileImageUrl: string | null;
  } | null;
  lastMessage?: Message;
  unreadCount?: number;
}

// Insert schemas
export const insertLandingPageSchema = zodInsertSchema(landingPages).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertLandingPageVersionSchema = zodInsertSchema(landingPageVersions).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertLandingPageElementSchema = zodInsertSchema(landingPageElements).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertLandingPageExperimentSchema = zodInsertSchema(landingPageExperiments).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertExperimentAssignmentSchema = zodInsertSchema(experimentAssignments).omit({
  id: true,
  assignedAt: true,
});

export const insertLandingPageAnalyticSchema = zodInsertSchema(landingPageAnalytics).omit({
  id: true,
  createdAt: true,
});

export const insertSuggestionSchema = zodInsertSchema(suggestions).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  plainContent: z.string().max(5000, "Suggestion text must be 5000 characters or fewer"),
});

export const insertNoteSchema = zodInsertSchema(notes).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  pinnedAt: true,
});

export const insertNoteLinkSchema = zodInsertSchema(noteLinks).omit({
  id: true,
  createdAt: true,
});

export const insertKanbanColumnSchema = zodInsertSchema(kanbanColumns).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertDashboardWidgetSchema = zodInsertSchema(dashboardWidgets).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertChecklistTemplateSchema = zodInsertSchema(checklistTemplates).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertChecklistSchema = zodInsertSchema(checklists).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  completedAt: true,
});

export const insertChecklistShareSchema = zodInsertSchema(checklistShares).omit({
  id: true,
  createdAt: true,
});

export const insertChecklistTemplateShareSchema = zodInsertSchema(checklistTemplateShares).omit({
  id: true,
  createdAt: true,
});

export const insertMediaFileSchema = zodInsertSchema(mediaFiles).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertGoalMediaSchema = zodInsertSchema(goalMedia).omit({
  id: true,
  createdAt: true,
});

export const insertTaskMediaSchema = zodInsertSchema(taskMedia).omit({
  id: true,
  createdAt: true,
});

export const insertTaskDriveFileSchema = zodInsertSchema(taskDriveFiles).omit({
  id: true,
  createdAt: true,
});

export const insertNoteMediaSchema = zodInsertSchema(noteMedia).omit({
  id: true,
  createdAt: true,
});

export const insertNoteDriveFileSchema = zodInsertSchema(noteDriveFiles).omit({
  id: true,
  createdAt: true,
});

export const insertCalendarEventMediaSchema = zodInsertSchema(calendarEventMedia).omit({
  id: true,
  createdAt: true,
});

export const insertCalendarEventDriveFileSchema = zodInsertSchema(calendarEventDriveFiles).omit({
  id: true,
  createdAt: true,
});

export const insertTaskUrlAttachmentSchema = zodInsertSchema(taskUrlAttachments).omit({
  id: true,
  createdAt: true,
}).refine(
  (data) => {
    try {
      const parsed = new URL(data.url);
      return parsed.protocol === "http:" || parsed.protocol === "https:";
    } catch {
      return false;
    }
  },
  { message: "URL must use http or https scheme", path: ["url"] }
);

export const insertNoteUrlAttachmentSchema = zodInsertSchema(noteUrlAttachments).omit({
  id: true,
  createdAt: true,
});

export const insertCalendarEventUrlAttachmentSchema = zodInsertSchema(calendarEventUrlAttachments).omit({
  id: true,
  createdAt: true,
});

export const insertGoalUrlAttachmentSchema = zodInsertSchema(goalUrlAttachments).omit({
  id: true,
  createdAt: true,
});

export const insertFriendshipSchema = zodInsertSchema(friendships).omit({
  id: true,
  createdAt: true,
});

export const insertFriendInvitationSchema = zodInsertSchema(friendInvitations).omit({
  id: true,
  createdAt: true,
  respondedAt: true,
});

export const insertFriendGroupSchema = zodInsertSchema(friendGroups).omit({
  id: true,
  createdAt: true,
});

export const insertFriendGroupMemberSchema = zodInsertSchema(friendGroupMembers).omit({
  id: true,
  createdAt: true,
});

export const insertGoalShareSchema = zodInsertSchema(goalShares).omit({
  id: true,
  createdAt: true,
  tenantId: true,  // Set server-side from session
  ownerId: true,   // Set server-side from session
});

export const insertTaskShareSchema = zodInsertSchema(taskShares).omit({
  id: true,
  createdAt: true,
  tenantId: true,  // Set server-side from session
  ownerId: true,   // Set server-side from session
});

export const insertNoteShareSchema = zodInsertSchema(noteShares).omit({
  id: true,
  createdAt: true,
  tenantId: true,  // Set server-side from session
  ownerId: true,   // Set server-side from session
});

export const insertMediaShareSchema = zodInsertSchema(mediaShares).omit({
  id: true,
  createdAt: true,
  tenantId: true,  // Set server-side from session
  ownerId: true,   // Set server-side from session
});

export const insertConversationSchema = zodInsertSchema(conversations).omit({
  id: true,
  createdAt: true,
  lastMessageAt: true,
});

export const insertConversationParticipantSchema = zodInsertSchema(conversationParticipants).omit({
  id: true,
  joinedAt: true,
});

export const insertMessageSchema = zodInsertSchema(messages).omit({
  id: true,
  createdAt: true,
});

export const insertDriveConnectionSchema = zodInsertSchema(driveConnections).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertDriveFileSchema = zodInsertSchema(driveFiles).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertYoutubeConnectionSchema = zodInsertSchema(youtubeConnections).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertMediaFileShareSchema = zodInsertSchema(mediaFileShares).omit({
  id: true,
  createdAt: true,
});

export const insertDriveFileShareSchema = zodInsertSchema(driveFileShares).omit({
  id: true,
  createdAt: true,
});

// Insert types
export type InsertLandingPage = z.infer<typeof insertLandingPageSchema>;
export type InsertLandingPageVersion = z.infer<typeof insertLandingPageVersionSchema>;
export type InsertLandingPageElement = z.infer<typeof insertLandingPageElementSchema>;
export type InsertLandingPageExperiment = z.infer<typeof insertLandingPageExperimentSchema>;
export type InsertExperimentAssignment = z.infer<typeof insertExperimentAssignmentSchema>;
export type InsertLandingPageAnalytic = z.infer<typeof insertLandingPageAnalyticSchema>;
export type InsertSuggestion = z.infer<typeof insertSuggestionSchema>;
export type InsertNote = z.infer<typeof insertNoteSchema>;
export type InsertNoteLink = z.infer<typeof insertNoteLinkSchema>;
export type InsertKanbanColumn = z.infer<typeof insertKanbanColumnSchema>;
export type DashboardWidget = typeof dashboardWidgets.$inferSelect;
export type InsertDashboardWidget = z.infer<typeof insertDashboardWidgetSchema>;
export type InsertChecklistTemplate = z.infer<typeof insertChecklistTemplateSchema>;
export type InsertChecklist = z.infer<typeof insertChecklistSchema>;
export type InsertChecklistShare = z.infer<typeof insertChecklistShareSchema>;
export type InsertChecklistTemplateShare = z.infer<typeof insertChecklistTemplateShareSchema>;
export type MediaFile = typeof mediaFiles.$inferSelect;
export type InsertMediaFile = z.infer<typeof insertMediaFileSchema>;
export type GoalMedia = typeof goalMedia.$inferSelect;
export type InsertGoalMedia = z.infer<typeof insertGoalMediaSchema>;
export type TaskMedia = typeof taskMedia.$inferSelect;
export type InsertTaskMedia = z.infer<typeof insertTaskMediaSchema>;
export type TaskDriveFile = typeof taskDriveFiles.$inferSelect;
export type InsertTaskDriveFile = z.infer<typeof insertTaskDriveFileSchema>;
export type NoteMedia = typeof noteMedia.$inferSelect;
export type InsertNoteMedia = z.infer<typeof insertNoteMediaSchema>;
export type NoteDriveFile = typeof noteDriveFiles.$inferSelect;
export type InsertNoteDriveFile = z.infer<typeof insertNoteDriveFileSchema>;
export type CalendarEventMedia = typeof calendarEventMedia.$inferSelect;
export type InsertCalendarEventMedia = z.infer<typeof insertCalendarEventMediaSchema>;
export type CalendarEventDriveFile = typeof calendarEventDriveFiles.$inferSelect;
export type InsertCalendarEventDriveFile = z.infer<typeof insertCalendarEventDriveFileSchema>;
export type TaskUrlAttachment = typeof taskUrlAttachments.$inferSelect;
export type InsertTaskUrlAttachment = z.infer<typeof insertTaskUrlAttachmentSchema>;
export type NoteUrlAttachment = typeof noteUrlAttachments.$inferSelect;
export type InsertNoteUrlAttachment = z.infer<typeof insertNoteUrlAttachmentSchema>;
export type CalendarEventUrlAttachment = typeof calendarEventUrlAttachments.$inferSelect;
export type InsertCalendarEventUrlAttachment = z.infer<typeof insertCalendarEventUrlAttachmentSchema>;
export type GoalUrlAttachment = typeof goalUrlAttachments.$inferSelect;
export type InsertGoalUrlAttachment = z.infer<typeof insertGoalUrlAttachmentSchema>;
export type InsertFriendship = z.infer<typeof insertFriendshipSchema>;
export type InsertFriendInvitation = z.infer<typeof insertFriendInvitationSchema>;
export type InsertFriendGroup = z.infer<typeof insertFriendGroupSchema>;
export type InsertFriendGroupMember = z.infer<typeof insertFriendGroupMemberSchema>;
export type InsertGoalShare = z.infer<typeof insertGoalShareSchema>;
export type InsertTaskShare = z.infer<typeof insertTaskShareSchema>;
export type InsertNoteShare = z.infer<typeof insertNoteShareSchema>;
export type InsertMediaShare = z.infer<typeof insertMediaShareSchema>;
export type InsertConversation = z.infer<typeof insertConversationSchema>;
export type InsertConversationParticipant = z.infer<typeof insertConversationParticipantSchema>;
export type InsertMessage = z.infer<typeof insertMessageSchema>;
export type DriveConnection = typeof driveConnections.$inferSelect;
export type InsertDriveConnection = z.infer<typeof insertDriveConnectionSchema>;
export type DriveFile = typeof driveFiles.$inferSelect;
export type InsertDriveFile = z.infer<typeof insertDriveFileSchema>;
export type MediaFileShare = typeof mediaFileShares.$inferSelect;
export type InsertMediaFileShare = z.infer<typeof insertMediaFileShareSchema>;
export type DriveFileShare = typeof driveFileShares.$inferSelect;
export type InsertDriveFileShare = z.infer<typeof insertDriveFileShareSchema>;
export type YoutubeConnection = typeof youtubeConnections.$inferSelect;
export type InsertYoutubeConnection = z.infer<typeof insertYoutubeConnectionSchema>;

// Tours tables for guided onboarding
export const tours = pgTable("tours", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  
  // Tour details
  title: text("title").notNull(),
  description: text("description"),
  targetPage: text("target_page").notNull(), // e.g., '/goals', '/tasks', '/dashboard'
  difficulty: text("difficulty").notNull().default("beginner"), // 'beginner', 'intermediate', 'advanced'
  estimatedMinutes: integer("estimated_minutes").notNull().default(5),
  
  // Appearance
  thumbnailUrl: text("thumbnail_url"), // Optional preview image
  iconName: text("icon_name"), // Lucide icon name
  primaryColor: text("primary_color").default("#3b82f6"),
  
  // Settings
  isActive: boolean("is_active").notNull().default(true),
  isRequired: boolean("is_required").notNull().default(false), // For mandatory onboarding tours
  sortOrder: integer("sort_order").notNull().default(0),
  
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

export const tourSteps = pgTable("tour_steps", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  tourId: varchar("tour_id").notNull().references(() => tours.id, { onDelete: "cascade" }),
  
  // Step details
  title: text("title").notNull(),
  description: text("description").notNull(), // Instructions for what to do
  videoUrl: text("video_url"), // YouTube/Vimeo embed URL
  
  // Interaction
  targetSelector: text("target_selector"), // CSS selector or data-testid for spotlight (e.g., '[data-testid="button-create-goal"]')
  actionType: text("action_type"), // 'click', 'input', 'select', 'navigate', 'observe'
  actionData: jsonb("action_data"), // Additional action configuration
  
  // Positioning
  popoverPosition: text("popover_position").notNull().default("bottom"), // 'top', 'bottom', 'left', 'right', 'center'
  spotlightPadding: integer("spotlight_padding").notNull().default(8), // Padding around highlighted element
  
  // Validation
  validationRule: text("validation_rule"), // Optional: How to validate step completion
  validationData: jsonb("validation_data"), // Data for validation (e.g., field value, API response)
  
  // Settings
  stepOrder: integer("step_order").notNull().default(0),
  isOptional: boolean("is_optional").notNull().default(false),
  autoAdvance: boolean("auto_advance").notNull().default(false), // Auto-advance after action completion
  delayMs: integer("delay_ms").notNull().default(0), // Delay before showing step
  
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

export const userTourProgress = pgTable("user_tour_progress", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  tourId: varchar("tour_id").notNull().references(() => tours.id, { onDelete: "cascade" }),
  
  // Progress tracking
  status: text("status").notNull().default("not_started"), // 'not_started', 'in_progress', 'completed', 'skipped'
  currentStepId: varchar("current_step_id").references(() => tourSteps.id, { onDelete: "set null" }),
  completedSteps: jsonb("completed_steps").notNull().default(sql`'[]'::jsonb`), // Array of completed step IDs
  
  // Timestamps
  startedAt: timestamp("started_at"),
  completedAt: timestamp("completed_at"),
  lastViewedAt: timestamp("last_viewed_at"),
  
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

// Insert schemas for tours
export const insertTourSchema = zodInsertSchema(tours).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertTourStepSchema = zodInsertSchema(tourSteps).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertUserTourProgressSchema = zodInsertSchema(userTourProgress).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Select types for tours
export type Tour = typeof tours.$inferSelect;
export type TourStep = typeof tourSteps.$inferSelect;
export type UserTourProgress = typeof userTourProgress.$inferSelect;

// Insert types for tours
export type InsertTour = z.infer<typeof insertTourSchema>;
export type InsertTourStep = z.infer<typeof insertTourStepSchema>;
export type InsertUserTourProgress = z.infer<typeof insertUserTourProgressSchema>;

// Helper types
export interface TourWithSteps extends Tour {
  steps: TourStep[];
  progress?: UserTourProgress;
}

// Helper function to derive display category from targetPage
export function getCategoryFromTargetPage(targetPage: string): string {
  const pageName = targetPage.replace(/^\//, '').split('/')[0];
  return pageName || 'general';
}

// Category labels for display
export const TOUR_CATEGORY_LABELS: Record<string, string> = {
  general: "General",
  dashboard: "Dashboard",
  goals: "Goals",
  tasks: "Tasks",
  habits: "Habits",
  journal: "Journal",
  notepad: "Notepad",
  planner: "Planner",
  calendar: "Calendar",
  meditation: "Meditation",
  analytics: "Analytics",
  checklists: "Checklists",
  gratefuls: "Gratefuls",
  admin: "Admin Panel",
};

export interface TourStepWithValidation extends TourStep {
  isCompleted: boolean;
  isCurrent: boolean;
}

// Testimonials table for user testimonials and reviews
export const testimonials = pgTable("testimonials", {
  id: varchar("id").primaryKey().$defaultFn(() => crypto.randomUUID()),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  
  // Testimonial content
  rating: integer("rating").notNull(), // 1-5 star rating
  testimonialText: text("testimonial_text").notNull(),
  videoUrl: text("video_url"), // Base64 or URL to uploaded video
  photoUrl: text("photo_url"), // Base64 or URL to user photo
  
  // User display info (captured at submission time)
  displayName: text("display_name").notNull(), // User's name for display
  
  // Moderation
  status: text("status").notNull().default("pending"), // 'pending', 'approved', 'rejected'
  adminNote: text("admin_note"), // Admin's note for rejection reason or internal use
  reviewedBy: varchar("reviewed_by").references(() => users.id, { onDelete: "set null" }),
  reviewedAt: timestamp("reviewed_at"),
  
  // Terms acceptance
  termsAccepted: boolean("terms_accepted").notNull().default(false),
  termsAcceptedAt: timestamp("terms_accepted_at"),
  
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

// Insert schema for testimonials
export const insertTestimonialSchema = zodInsertSchema(testimonials).omit({
  id: true,
  status: true,
  adminNote: true,
  reviewedBy: true,
  reviewedAt: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  videoUrl: z.string().url().refine(
    (url) => /^https?:\/\//i.test(url),
    { message: "Video URL must use http or https" }
  ).nullish(),
});

// Select type for testimonials
export type Testimonial = typeof testimonials.$inferSelect;

// Insert type for testimonials
export type InsertTestimonial = z.infer<typeof insertTestimonialSchema>;

// Helper type for testimonial with user info
export interface TestimonialWithUser extends Testimonial {
  user?: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    profileImageUrl: string | null;
  };
  reviewer?: {
    id: string;
    firstName: string | null;
    lastName: string | null;
  };
}

// Chat Rooms for group messaging
export const chatRooms = pgTable("chat_rooms", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  description: text("description"),
  createdById: varchar("created_by_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

export const chatRoomMembers = pgTable("chat_room_members", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  chatRoomId: varchar("chat_room_id").notNull().references(() => chatRooms.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  role: text("role").notNull().default("member"), // 'admin', 'member'
  joinedAt: timestamp("joined_at").notNull().default(sql`now()`),
});

export const chatRoomMessages = pgTable("chat_room_messages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  chatRoomId: varchar("chat_room_id").notNull().references(() => chatRooms.id, { onDelete: "cascade" }),
  senderId: varchar("sender_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  content: text("content").notNull(),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

// Insert schemas for chat rooms
export const insertChatRoomSchema = zodInsertSchema(chatRooms).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertChatRoomMemberSchema = zodInsertSchema(chatRoomMembers).omit({
  id: true,
  joinedAt: true,
});

export const insertChatRoomMessageSchema = zodInsertSchema(chatRoomMessages).omit({
  id: true,
  createdAt: true,
});

// Types for chat rooms
export type ChatRoom = typeof chatRooms.$inferSelect;
export type InsertChatRoom = z.infer<typeof insertChatRoomSchema>;
export type ChatRoomMember = typeof chatRoomMembers.$inferSelect;
export type InsertChatRoomMember = z.infer<typeof insertChatRoomMemberSchema>;
export type ChatRoomMessage = typeof chatRoomMessages.$inferSelect;
export type InsertChatRoomMessage = z.infer<typeof insertChatRoomMessageSchema>;

// Helper types for chat rooms with members
export interface ChatRoomWithMembers extends ChatRoom {
  members: Array<{
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    role: string;
  }>;
  createdBy: {
    id: string;
    firstName: string | null;
    lastName: string | null;
  };
  unreadCount?: number;
  lastMessageAt?: string;
}

export interface ChatRoomMessageWithSender extends ChatRoomMessage {
  sender: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
  };
}

// ==================== NOTIFICATION SYSTEM ====================

// Notification Preferences - stores user contact info and global channel toggles
export const notificationPreferences = pgTable("notification_preferences", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  
  // Contact info
  mobilePhone: text("mobile_phone"),
  
  // Global channel toggles
  emailEnabled: boolean("email_enabled").notNull().default(true),
  smsEnabled: boolean("sms_enabled").notNull().default(false),
  inAppEnabled: boolean("in_app_enabled").notNull().default(true),
  
  // Daily summary settings
  dailySummaryEnabled: boolean("daily_summary_enabled").notNull().default(false),
  dailySummaryFrequency: text("daily_summary_frequency").notNull().default("daily"), // 'daily', 'weekly', 'monthly'
  dailySummaryTime: text("daily_summary_time").notNull().default("09:00"), // HH:MM format
  dailySummaryIncludeHabits: boolean("daily_summary_include_habits").notNull().default(true),
  dailySummaryIncludeTasks: boolean("daily_summary_include_tasks").notNull().default(true),
  dailySummaryIncludeEvents: boolean("daily_summary_include_events").notNull().default(true),
  dailySummaryIncludeGoals: boolean("daily_summary_include_goals").notNull().default(true),
  
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

// Notification Rules - defines what events trigger notifications and how
export const notificationRules = pgTable("notification_rules", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  
  // Event type: 'task_due', 'calendar_event', 'community_message', 'goal_deadline', 'habit_reminder'
  eventType: text("event_type").notNull(),
  
  // Enabled flag for this rule
  enabled: boolean("enabled").notNull().default(true),
  
  // Channels for this rule
  notifyByEmail: boolean("notify_by_email").notNull().default(true),
  notifyBySms: boolean("notify_by_sms").notNull().default(false),
  notifyByInApp: boolean("notify_by_in_app").notNull().default(true),
  
  // Timing settings (for time-based events)
  reminderMinutesBefore: integer("reminder_minutes_before").default(30), // minutes before event
  repeatIfIncomplete: boolean("repeat_if_incomplete").notNull().default(false), // for tasks
  repeatIntervalMinutes: integer("repeat_interval_minutes").default(60), // how often to repeat
  
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

// Scheduled Notifications - queue of pending notifications to be sent
export const scheduledNotifications = pgTable("scheduled_notifications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  
  // What triggered this notification
  ruleId: varchar("rule_id").references(() => notificationRules.id, { onDelete: "cascade" }),
  eventType: text("event_type").notNull(),
  
  // Reference to the source entity
  entityType: text("entity_type").notNull(), // 'task', 'event', 'message', 'goal', 'habit'
  entityId: varchar("entity_id").notNull(),
  
  // Notification content
  title: text("title").notNull(),
  message: text("message").notNull(),
  
  // Delivery settings
  channels: text("channels").array().notNull(), // ['email', 'sms', 'in_app']
  scheduledFor: timestamp("scheduled_for").notNull(),
  
  // Status
  status: text("status").notNull().default("pending"), // 'pending', 'sent', 'failed', 'cancelled'
  sentAt: timestamp("sent_at"),
  errorMessage: text("error_message"),
  
  // For repeat notifications
  isRepeat: boolean("is_repeat").notNull().default(false),
  repeatCount: integer("repeat_count").notNull().default(0),
  
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

// In-App Notifications - notifications shown in the app
export const inAppNotifications = pgTable("in_app_notifications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  
  // Content
  title: text("title").notNull(),
  message: text("message").notNull(),
  type: text("type").notNull().default("info"), // 'info', 'warning', 'success', 'error'
  
  // Link to related entity
  entityType: text("entity_type"), // 'task', 'event', 'message', 'goal', 'habit'
  entityId: varchar("entity_id"),
  actionUrl: text("action_url"), // URL to navigate to when clicked
  
  // Status
  isRead: boolean("is_read").notNull().default(false),
  readAt: timestamp("read_at"),
  isDismissed: boolean("is_dismissed").notNull().default(false),
  
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

// Insert schemas
export const insertNotificationPreferencesSchema = zodInsertSchema(notificationPreferences).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertNotificationRuleSchema = zodInsertSchema(notificationRules).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertScheduledNotificationSchema = zodInsertSchema(scheduledNotifications).omit({
  id: true,
  createdAt: true,
});

export const insertInAppNotificationSchema = zodInsertSchema(inAppNotifications).omit({
  id: true,
  createdAt: true,
});

// Types
export type NotificationPreferences = typeof notificationPreferences.$inferSelect;
export type InsertNotificationPreferences = z.infer<typeof insertNotificationPreferencesSchema>;
export type NotificationRule = typeof notificationRules.$inferSelect;
export type InsertNotificationRule = z.infer<typeof insertNotificationRuleSchema>;
export type ScheduledNotification = typeof scheduledNotifications.$inferSelect;
export type InsertScheduledNotification = z.infer<typeof insertScheduledNotificationSchema>;
export type InAppNotification = typeof inAppNotifications.$inferSelect;
export type InsertInAppNotification = z.infer<typeof insertInAppNotificationSchema>;

// ============================================
// AI COACH ANALYSES
// ============================================

export const aiCoachAnalyses = pgTable("ai_coach_analyses", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  title: varchar("title", { length: 255 }).notNull(),
  selectedSources: jsonb("selected_sources").notNull(),
  selectedConstraints: jsonb("selected_constraints").notNull(),
  metadata: jsonb("metadata"),
  summaryHtml: text("summary_html"),
  status: varchar("status", { length: 50 }).notNull().default("pending"),
});

export const insertAiCoachAnalysisSchema = zodInsertSchema(aiCoachAnalyses).omit({ 
  id: true, 
  createdAt: true 
});
export type InsertAiCoachAnalysis = z.infer<typeof insertAiCoachAnalysisSchema>;
export type AiCoachAnalysis = typeof aiCoachAnalyses.$inferSelect;

// ============================================
// ASSISTANT/VIRTUAL ASSISTANT SYSTEM
// ============================================

// Assistant profiles - links assistant users to their manager
export const assistantProfiles = pgTable("assistant_profiles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }), // The assistant user; one person may have relationships with multiple managers
  managerUserId: varchar("manager_user_id").notNull().references(() => users.id, { onDelete: "cascade" }), // The user who manages this assistant
  assistantType: text("assistant_type").notNull().default("assistant"), // 'assistant' or 'virtual_assistant'
  
  // Permissions - which modules the assistant can access
  permissions: jsonb("permissions").notNull().default('{"notes":false,"goals":false,"calendar":false,"tasks":false,"notepad":false,"habits":false,"trackerX":false}'),
  
  // Status and employment info
  status: text("status").notNull().default("active"), // 'active', 'suspended', 'inactive'
  hourlyRate: doublePrecision("hourly_rate"), // Optional hourly rate for billing
  
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

// Calendar event shares - sharing calendar events with assistants/friends
export const calendarEventShares = pgTable("calendar_event_shares", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  calendarEventId: varchar("calendar_event_id").notNull().references(() => calendarEvents.id, { onDelete: "cascade" }),
  ownerId: varchar("owner_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  sharedWithId: varchar("shared_with_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  permission: text("permission").notNull().default("view"), // 'view', 'edit'
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

// Assistant time entries - time tracking for assistants
export const assistantTimeEntries = pgTable("assistant_time_entries", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  assistantUserId: varchar("assistant_user_id").notNull().references(() => users.id, { onDelete: "cascade" }), // The assistant who logged time
  managerUserId: varchar("manager_user_id").notNull().references(() => users.id, { onDelete: "cascade" }), // The manager/owner
  
  // What they worked on (optional links)
  taskId: varchar("task_id").references(() => tasks.id, { onDelete: "set null" }),
  goalId: varchar("goal_id").references(() => goals.id, { onDelete: "set null" }),
  
  // Time tracking
  clockInAt: timestamp("clock_in_at").notNull(),
  clockOutAt: timestamp("clock_out_at"), // Null means currently clocked in
  durationMinutes: integer("duration_minutes"), // Calculated on clock out
  
  // Additional info
  description: text("description"), // What they worked on
  billingCode: text("billing_code"), // Optional billing/project code
  
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

// Assistant invitations - for inviting new assistants
export const assistantInvitations = pgTable("assistant_invitations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  inviterUserId: varchar("inviter_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  email: varchar("email").notNull(),
  firstName: varchar("first_name"),
  lastName: varchar("last_name"),
  assistantType: text("assistant_type").notNull().default("assistant"), // 'assistant' or 'virtual_assistant'
  permissions: jsonb("permissions").notNull().default('{"notes":false,"goals":false,"calendar":false,"tasks":false,"notepad":false,"habits":false,"trackerX":false}'),
  hourlyRate: doublePrecision("hourly_rate"),
  dailyBudgetCents: integer("daily_budget_cents"),
  token: varchar("token").notNull().unique(),
  status: text("status").notNull().default("pending"), // 'pending', 'accepted', 'expired', 'cancelled'
  expiresAt: timestamp("expires_at").notNull(),
  acceptedAt: timestamp("accepted_at"),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

// Insert schemas for assistant system
export const insertAssistantProfileSchema = zodInsertSchema(assistantProfiles).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertCalendarEventShareSchema = zodInsertSchema(calendarEventShares).omit({
  id: true,
  createdAt: true,
  tenantId: true,  // Set server-side from session
  ownerId: true,   // Set server-side from session
});

export const insertAssistantTimeEntrySchema = zodInsertSchema(assistantTimeEntries).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertAssistantInvitationSchema = zodInsertSchema(assistantInvitations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Types for assistant system
export type AssistantProfile = typeof assistantProfiles.$inferSelect;
export type InsertAssistantProfile = z.infer<typeof insertAssistantProfileSchema>;
export type CalendarEventShare = typeof calendarEventShares.$inferSelect;
export type InsertCalendarEventShare = z.infer<typeof insertCalendarEventShareSchema>;
export type AssistantTimeEntry = typeof assistantTimeEntries.$inferSelect;
export type InsertAssistantTimeEntry = z.infer<typeof insertAssistantTimeEntrySchema>;
export type AssistantInvitation = typeof assistantInvitations.$inferSelect;
export type InsertAssistantInvitation = z.infer<typeof insertAssistantInvitationSchema>;

// Helper type for assistant permissions
export interface AssistantPermissions {
  notes: boolean;
  goals: boolean;
  calendar: boolean;
  tasks: boolean;
  notepad: boolean;
  habits: boolean;
  trackerX: boolean;
  contacts: boolean;
  ai: boolean;
  mcp: boolean;
  retellInbound: boolean;
  retellOutbound: boolean;
  vault: boolean;
}

// Helper types for assistant with user info
export interface AssistantWithUser extends AssistantProfile {
  user: {
    id: string;
    email: string | null;
    username: string | null;
    firstName: string | null;
    lastName: string | null;
    profileImageUrl: string | null;
  };
}

export interface TimeEntryWithDetails extends AssistantTimeEntry {
  task?: { id: string; title: string } | null;
  goal?: { id: string; title: string } | null;
  assistant?: { id: string; firstName: string | null; lastName: string | null; email: string | null };
}

// Important dates - for tracking recurring events like birthdays, anniversaries, etc.
export const importantDates = pgTable("important_dates", {
  id: varchar("id").primaryKey().$defaultFn(() => crypto.randomUUID()),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  description: text("description").notNull(),
  eventDate: timestamp("event_date").notNull(),
  isRecurring: boolean("is_recurring").notNull().default(false),
  recurrenceType: text("recurrence_type"), // 'weekly', 'monthly', 'quarterly', 'annually'
  isGift: boolean("is_gift").notNull().default(false),
  addToCalendar: boolean("add_to_calendar").notNull().default(false),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

export const insertImportantDateSchema = zodInsertSchema(importantDates).omit({
  id: true,
  sortOrder: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertImportantDate = z.infer<typeof insertImportantDateSchema>;
export type ImportantDate = typeof importantDates.$inferSelect;

// Event type constants
export const NOTIFICATION_EVENT_TYPES = {
  TASK_DUE: 'task_due',
  CALENDAR_EVENT: 'calendar_event',
  COMMUNITY_MESSAGE: 'community_message',
  COMMUNITY_GROUP_MESSAGE: 'community_group_message',
  GOAL_DEADLINE: 'goal_deadline',
  HABIT_REMINDER: 'habit_reminder',
  FRIEND_REQUEST: 'friend_request',
} as const;

// Reminder time presets (in minutes)
export const REMINDER_PRESETS = [
  { label: '5 minutes before', value: 5 },
  { label: '15 minutes before', value: 15 },
  { label: '30 minutes before', value: 30 },
  { label: '1 hour before', value: 60 },
  { label: '2 hours before', value: 120 },
  { label: '1 day before', value: 1440 },
  { label: '2 days before', value: 2880 },
  { label: '1 week before', value: 10080 },
] as const;

// Time Budget System - for realistic goal planning
export const timeBudgets = pgTable("time_budgets", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  timeSpan: text("time_span"), // 'month', 'quarter', 'year'
  startDate: text("start_date"), // User-entered start date string
  endDate: text("end_date"), // User-entered end date string
  isActive: boolean("is_active").notNull().default(false),
  wakeTime: text("wake_time"), // HH:MM format
  sleepTime: text("sleep_time"), // HH:MM format
  productiveTimeOfDay: text("productive_time_of_day"), // 'morning', 'afternoon', 'evening'
  hasJob: boolean("has_job").notNull().default(false),
  hasChildren: boolean("has_children").notNull().default(false),
  isWritingBook: boolean("is_writing_book").notNull().default(false),
  bookWritingHours: integer("book_writing_hours"),
  bookWritingTimeOfDay: text("book_writing_time_of_day"), // 'morning', 'afternoon', 'evening'
  bookWritingDays: jsonb("book_writing_days"), // Array of day names
  isCreatingContent: boolean("is_creating_content").notNull().default(false),
  contentCreationHours: integer("content_creation_hours"),
  contentCreationTimeOfDay: text("content_creation_time_of_day"),
  contentCreationDays: jsonb("content_creation_days"),
  tvGamingEnabled: boolean("tv_gaming_enabled").notNull().default(false),
  tvGamingDays: jsonb("tv_gaming_days"),
  tvGamingTimeOfDay: text("tv_gaming_time_of_day"),
  aiAnalysis: jsonb("ai_analysis"), // AI-generated time budget analysis and recommendations
  totalAvailableMinutesPerWeek: integer("total_available_minutes_per_week"),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

export const timeBudgetReligiousHolidays = pgTable("time_budget_religious_holidays", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  timeBudgetId: varchar("time_budget_id").notNull().references(() => timeBudgets.id, { onDelete: "cascade" }),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  holidayName: text("holiday_name").notNull(),
  religion: text("religion"), // Optional categorization
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

export const timeBudgetJobSchedules = pgTable("time_budget_job_schedules", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  timeBudgetId: varchar("time_budget_id").notNull().references(() => timeBudgets.id, { onDelete: "cascade" }),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  dayOfWeek: text("day_of_week").notNull(), // 'monday', 'tuesday', etc.
  startTime: text("start_time").notNull(), // HH:MM format
  endTime: text("end_time").notNull(), // HH:MM format
  description: text("description"),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

export const timeBudgetChildCommitments = pgTable("time_budget_child_commitments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  timeBudgetId: varchar("time_budget_id").notNull().references(() => timeBudgets.id, { onDelete: "cascade" }),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  description: text("description").notNull(), // 'Drop Off at School', 'Pick Up at School', 'After school activity', etc.
  dayOfWeek: text("day_of_week").notNull(),
  startTime: text("start_time").notNull(),
  endTime: text("end_time").notNull(),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

export const timeBudgetPersonalActivities = pgTable("time_budget_personal_activities", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  timeBudgetId: varchar("time_budget_id").notNull().references(() => timeBudgets.id, { onDelete: "cascade" }),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  description: text("description").notNull(), // 'Meditation', 'Gym', 'Yoga', 'Reading', etc.
  dayOfWeek: text("day_of_week").notNull(),
  startTime: text("start_time").notNull(),
  endTime: text("end_time").notNull(),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

export const timeBudgetTravelEvents = pgTable("time_budget_travel_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  timeBudgetId: varchar("time_budget_id").notNull().references(() => timeBudgets.id, { onDelete: "cascade" }),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  description: text("description").notNull(), // 'Travel to Spain', 'Go to parents house for Christmas'
  startDate: timestamp("start_date").notNull(),
  endDate: timestamp("end_date").notNull(),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

// Insert schemas for time budget tables
export const insertTimeBudgetSchema = zodInsertSchema(timeBudgets).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertTimeBudgetReligiousHolidaySchema = zodInsertSchema(timeBudgetReligiousHolidays).omit({
  id: true,
  createdAt: true,
});

export const insertTimeBudgetJobScheduleSchema = zodInsertSchema(timeBudgetJobSchedules).omit({
  id: true,
  createdAt: true,
});

export const insertTimeBudgetChildCommitmentSchema = zodInsertSchema(timeBudgetChildCommitments).omit({
  id: true,
  sortOrder: true,
  createdAt: true,
});

export const insertTimeBudgetPersonalActivitySchema = zodInsertSchema(timeBudgetPersonalActivities).omit({
  id: true,
  sortOrder: true,
  createdAt: true,
});

export const insertTimeBudgetTravelEventSchema = zodInsertSchema(timeBudgetTravelEvents).omit({
  id: true,
  sortOrder: true,
  createdAt: true,
});

// Time budget goal allocations - links goals to time budgets with allocation details
export const timeBudgetGoals = pgTable("time_budget_goals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  timeBudgetId: varchar("time_budget_id").notNull().references(() => timeBudgets.id, { onDelete: "cascade" }),
  goalId: varchar("goal_id").notNull().references(() => goals.id, { onDelete: "cascade" }),
  requiredMinutesPerWeek: integer("required_minutes_per_week").notNull().default(0),
  allocatedMinutesPerWeek: integer("allocated_minutes_per_week").notNull().default(0),
  allocationStatus: text("allocation_status").notNull().default("pending"), // 'pending', 'allocated', 'partial', 'conflict'
  preferredDays: jsonb("preferred_days"), // Array of day names user prefers for this goal
  preferredTimeOfDay: text("preferred_time_of_day"), // 'morning', 'afternoon', 'evening'
  scheduledBlocks: jsonb("scheduled_blocks"), // Array of {dayOfWeek, startTime, endTime} for finalized blocks
  bookOnCalendar: boolean("book_on_calendar").notNull().default(false),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

export const insertTimeBudgetGoalSchema = zodInsertSchema(timeBudgetGoals).omit({
  id: true,
  sortOrder: true,
  createdAt: true,
  updatedAt: true,
});

// Types for time budget
export type TimeBudget = typeof timeBudgets.$inferSelect;
export type InsertTimeBudget = z.infer<typeof insertTimeBudgetSchema>;
export type TimeBudgetReligiousHoliday = typeof timeBudgetReligiousHolidays.$inferSelect;
export type InsertTimeBudgetReligiousHoliday = z.infer<typeof insertTimeBudgetReligiousHolidaySchema>;
export type TimeBudgetJobSchedule = typeof timeBudgetJobSchedules.$inferSelect;
export type InsertTimeBudgetJobSchedule = z.infer<typeof insertTimeBudgetJobScheduleSchema>;
export type TimeBudgetChildCommitment = typeof timeBudgetChildCommitments.$inferSelect;
export type InsertTimeBudgetChildCommitment = z.infer<typeof insertTimeBudgetChildCommitmentSchema>;
export type TimeBudgetPersonalActivity = typeof timeBudgetPersonalActivities.$inferSelect;
export type InsertTimeBudgetPersonalActivity = z.infer<typeof insertTimeBudgetPersonalActivitySchema>;
export type TimeBudgetTravelEvent = typeof timeBudgetTravelEvents.$inferSelect;
export type InsertTimeBudgetTravelEvent = z.infer<typeof insertTimeBudgetTravelEventSchema>;
export type TimeBudgetGoal = typeof timeBudgetGoals.$inferSelect;
export type InsertTimeBudgetGoal = z.infer<typeof insertTimeBudgetGoalSchema>;

// Preset religious holidays list
export const RELIGIOUS_HOLIDAYS = [
  // Christian
  { name: 'Christmas Day', religion: 'Christian' },
  { name: 'Easter Sunday', religion: 'Christian' },
  { name: 'Good Friday', religion: 'Christian' },
  { name: 'Ash Wednesday', religion: 'Christian' },
  { name: 'Palm Sunday', religion: 'Christian' },
  { name: 'Pentecost', religion: 'Christian' },
  { name: 'All Saints Day', religion: 'Christian' },
  // Jewish
  { name: 'Rosh Hashanah', religion: 'Jewish' },
  { name: 'Yom Kippur', religion: 'Jewish' },
  { name: 'Passover', religion: 'Jewish' },
  { name: 'Hanukkah', religion: 'Jewish' },
  { name: 'Sukkot', religion: 'Jewish' },
  { name: 'Shavuot', religion: 'Jewish' },
  { name: 'Purim', religion: 'Jewish' },
  // Islamic
  { name: 'Eid al-Fitr', religion: 'Islamic' },
  { name: 'Eid al-Adha', religion: 'Islamic' },
  { name: 'Ramadan (Start)', religion: 'Islamic' },
  { name: 'Mawlid al-Nabi', religion: 'Islamic' },
  { name: 'Laylat al-Qadr', religion: 'Islamic' },
  // Hindu
  { name: 'Diwali', religion: 'Hindu' },
  { name: 'Holi', religion: 'Hindu' },
  { name: 'Navratri', religion: 'Hindu' },
  { name: 'Dussehra', religion: 'Hindu' },
  { name: 'Ganesh Chaturthi', religion: 'Hindu' },
  { name: 'Raksha Bandhan', religion: 'Hindu' },
  // Buddhist
  { name: 'Vesak (Buddha Day)', religion: 'Buddhist' },
  { name: 'Bodhi Day', religion: 'Buddhist' },
  { name: 'Parinirvana Day', religion: 'Buddhist' },
  { name: 'Dharma Day', religion: 'Buddhist' },
  // Sikh
  { name: 'Vaisakhi', religion: 'Sikh' },
  { name: 'Guru Nanak Jayanti', religion: 'Sikh' },
  { name: 'Lohri', religion: 'Sikh' },
  // Other
  { name: 'Chinese New Year', religion: 'Other' },
  { name: 'Thanksgiving', religion: 'Secular' },
  { name: 'New Years Day', religion: 'Secular' },
] as const;

// Preset child activity types
export const CHILD_ACTIVITY_TYPES = [
  'Drop Off at School',
  'Pick Up at School',
  'After School Activity - Soccer',
  'After School Activity - Dance',
  'After School Activity - Music',
  'After School Activity - Sports',
  'After School Activity - Tutoring',
  'Homework Help',
  'Bedtime Routine',
  'Morning Routine',
  'Doctor Appointment',
  'Other',
] as const;

// Preset personal activity types
export const PERSONAL_ACTIVITY_TYPES = [
  'Meditation',
  'Gym',
  'Yoga',
  'Reading',
  'Exercise',
  'Walking',
  'Running',
  'Swimming',
  'Therapy',
  'Doctor Appointment',
  'Self-Care',
  'Hobbies',
  'Other',
] as const;

// Preset travel event types
export const TRAVEL_EVENT_TYPES = [
  'Family Visit',
  'Vacation',
  'Business Trip',
  'Holiday Travel',
  'Weekend Getaway',
  'Wedding',
  'Conference',
  'Other',
] as const;

// AI Coach Conversations - saved chat sessions
export const aiCoachConversations = pgTable("ai_coach_conversations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  title: text("title").notNull(),
  isArchived: boolean("is_archived").notNull().default(false),
  lastMessageAt: timestamp("last_message_at").notNull().default(sql`now()`),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

// AI Coach Messages - individual messages in a conversation
export const aiCoachMessages = pgTable("ai_coach_messages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  conversationId: varchar("conversation_id").notNull().references(() => aiCoachConversations.id, { onDelete: "cascade" }),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  role: text("role").notNull(), // 'user' or 'assistant'
  content: text("content").notNull(),
  toolCallsMade: jsonb("tool_calls_made").$type<Array<{ tool: string; ok?: boolean }>>(),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

// Insert schemas for AI Coach conversations
export const insertAiCoachConversationSchema = zodInsertSchema(aiCoachConversations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  lastMessageAt: true,
});

export const insertAiCoachMessageSchema = zodInsertSchema(aiCoachMessages).omit({
  id: true,
  createdAt: true,
});

// Types for AI Coach conversations
export type AiCoachConversation = typeof aiCoachConversations.$inferSelect;
export type InsertAiCoachConversation = z.infer<typeof insertAiCoachConversationSchema>;
export type AiCoachMessage = typeof aiCoachMessages.$inferSelect;
export type InsertAiCoachMessage = z.infer<typeof insertAiCoachMessageSchema>;

// ============================================
// CALENDAR SHARING SYSTEM
// ============================================

// Calendar Shares - share entire calendar with friends (like Google Calendar)
export const calendarShares = pgTable("calendar_shares", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  ownerId: varchar("owner_id").notNull().references(() => users.id, { onDelete: "cascade" }), // The user who owns the calendar
  sharedWithId: varchar("shared_with_id").notNull().references(() => users.id, { onDelete: "cascade" }), // The friend/assistant receiving access
  externalCalendarId: varchar("external_calendar_id").references(() => externalCalendars.id, { onDelete: "cascade" }), // NULL = the owner's whole internal calendar; set = one specific connected calendar
  permission: text("permission").notNull().default("view"), // 'view' or 'edit'
  color: varchar("color", { length: 7 }), // Hex color like "#3B82F6" for display
  label: text("label"), // Optional display name for the shared calendar
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

// Calendar Share Visibility - track which shared calendars each user wants to see
export const calendarShareVisibility = pgTable("calendar_share_visibility", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }), // The viewer
  calendarShareId: varchar("calendar_share_id").notNull().references(() => calendarShares.id, { onDelete: "cascade" }),
  isVisible: boolean("is_visible").notNull().default(true), // Toggle on/off
  customColor: varchar("custom_color", { length: 7 }), // Optional override color
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

// Insert schemas for calendar sharing
export const insertCalendarShareSchema = zodInsertSchema(calendarShares).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertCalendarShareVisibilitySchema = zodInsertSchema(calendarShareVisibility).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Types for calendar sharing
export type CalendarShare = typeof calendarShares.$inferSelect;
export type InsertCalendarShare = z.infer<typeof insertCalendarShareSchema>;
export type CalendarShareVisibility = typeof calendarShareVisibility.$inferSelect;
export type InsertCalendarShareVisibility = z.infer<typeof insertCalendarShareVisibilitySchema>;

// Helper type for shared calendar with owner info
export interface SharedCalendarWithOwner extends CalendarShare {
  owner: {
    id: string;
    email: string | null;
    username: string | null;
    firstName: string | null;
    lastName: string | null;
    profileImageUrl: string | null;
  };
  visibility?: CalendarShareVisibility;
  /** Populated when the share targets one specific connected external calendar. */
  externalCalendar?: { id: string; name: string; color: string | null } | null;
}

// Incantations
export const incantations = pgTable("incantations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  content: text("content").notNull().default(""),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
}, (table) => [
  index("incantations_tenant_user_idx").on(table.tenantId, table.userId),
]);

export const incantationReads = pgTable("incantation_reads", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  readAt: timestamp("read_at").notNull().default(sql`now()`),
}, (table) => [
  index("incantation_reads_tenant_user_idx").on(table.tenantId, table.userId),
]);

export const insertIncantationSchema = zodInsertSchema(incantations).omit({
  id: true,
  updatedAt: true,
});

export type Incantation = typeof incantations.$inferSelect;
export type InsertIncantation = z.infer<typeof insertIncantationSchema>;
export type IncantationRead = typeof incantationReads.$inferSelect;

// ─── Invoices ──────────────────────────────────────────────────────────────
export const invoices = pgTable("invoices", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  stripeInvoiceId: text("stripe_invoice_id").unique(),
  invoiceNumber: text("invoice_number"),
  status: text("status").notNull().default("open"), // draft | open | paid | void | uncollectible
  amountDue: integer("amount_due").notNull().default(0), // in cents
  amountPaid: integer("amount_paid").notNull().default(0), // in cents
  currency: text("currency").notNull().default("usd"),
  periodStart: timestamp("period_start"),
  periodEnd: timestamp("period_end"),
  paidAt: timestamp("paid_at"),
  hostedInvoiceUrl: text("hosted_invoice_url"),
  invoicePdfUrl: text("invoice_pdf_url"),
  lineItems: jsonb("line_items"),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
}, (table) => [
  index("invoices_tenant_idx").on(table.tenantId),
  index("invoices_stripe_id_idx").on(table.stripeInvoiceId),
]);

export const insertInvoiceSchema = zodInsertSchema(invoices).omit({ id: true, createdAt: true });
export type Invoice = typeof invoices.$inferSelect;
export type InsertInvoice = z.infer<typeof insertInvoiceSchema>;

// ─── Dunning Configs ────────────────────────────────────────────────────────
export const dunningConfigs = pgTable("dunning_configs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").references(() => tenants.id, { onDelete: "cascade" }), // null = global default
  retryDays: jsonb("retry_days").notNull().default(sql`'[1,3,7,14]'::jsonb`), // e.g. [1, 3, 7, 14]
  gracePeriodDays: integer("grace_period_days").notNull().default(5),
  stageActions: jsonb("stage_actions").notNull().default(sql`'{}'::jsonb`), // { "0": "notify", "1": "restrict", "2": "suspend" }
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

export const insertDunningConfigSchema = zodInsertSchema(dunningConfigs).omit({ id: true, createdAt: true, updatedAt: true });
export type DunningConfig = typeof dunningConfigs.$inferSelect;
export type InsertDunningConfig = z.infer<typeof insertDunningConfigSchema>;

// ─── Audit Logs ─────────────────────────────────────────────────────────────
export const auditLogs = pgTable("audit_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").references(() => tenants.id, { onDelete: "set null" }),
  actorUserId: varchar("actor_user_id"),
  actorEmail: text("actor_email"),
  action: text("action").notNull(), // e.g. "tenant.freeze", "user.role_change"
  targetType: text("target_type"), // "tenant" | "user" | "coupon" | "plan"
  targetId: text("target_id"),
  targetDescription: text("target_description"),
  beforeValue: jsonb("before_value"),
  afterValue: jsonb("after_value"),
  ipAddress: text("ip_address"),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
}, (table) => [
  index("audit_logs_tenant_idx").on(table.tenantId),
  index("audit_logs_actor_idx").on(table.actorUserId),
  index("audit_logs_created_idx").on(table.createdAt),
]);

export const insertAuditLogSchema = zodInsertSchema(auditLogs).omit({ id: true, createdAt: true });
export type AuditLog = typeof auditLogs.$inferSelect;
export type InsertAuditLog = z.infer<typeof insertAuditLogSchema>;

// ─── API Keys ────────────────────────────────────────────────────────────────
export const apiKeys = pgTable("api_keys", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  keyHash: text("key_hash").notNull(), // bcrypt hash
  keyPrefix: text("key_prefix").notNull(), // first 8 chars for display
  scopes: text("scopes").array().notNull().default(sql`'{}'::text[]`),
  lastUsedAt: timestamp("last_used_at"),
  expiresAt: timestamp("expires_at"),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
}, (table) => [
  index("api_keys_tenant_idx").on(table.tenantId),
  index("api_keys_prefix_idx").on(table.keyPrefix),
]);

export const insertApiKeySchema = zodInsertSchema(apiKeys).omit({ id: true, createdAt: true });
export type ApiKey = typeof apiKeys.$inferSelect;
export type InsertApiKey = z.infer<typeof insertApiKeySchema>;

// ========================
// HEALTH MODULE
// ========================

// Food items (system database + user custom foods)
export const foodItems = pgTable("food_items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").references(() => users.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  brand: text("brand"),
  servingSize: real("serving_size").notNull().default(100),
  servingUnit: text("serving_unit").notNull().default("g"),
  calories: real("calories").notNull().default(0),
  protein: real("protein").notNull().default(0),
  carbs: real("carbs").notNull().default(0),
  fat: real("fat").notNull().default(0),
  fiber: real("fiber").notNull().default(0),
  sugar: real("sugar").notNull().default(0),
  saturatedFat: real("saturated_fat").notNull().default(0),
  transFat: real("trans_fat").notNull().default(0),
  sodium: real("sodium").notNull().default(0),
  isCustom: boolean("is_custom").notNull().default(false),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
}, (table) => [
  index("food_items_tenant_idx").on(table.tenantId),
  index("food_items_name_idx").on(table.name),
]);

// Daily meal diary
export const mealDiaryEntries = pgTable("meal_diary_entries", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  date: text("date").notNull(),
  mealType: text("meal_type").notNull(), // breakfast, lunch, dinner, snacks
  foodName: text("food_name").notNull(),
  foodItemId: varchar("food_item_id").references(() => foodItems.id, { onDelete: "set null" }),
  quantity: real("quantity").notNull().default(1),
  servingUnit: text("serving_unit").notNull().default("serving"),
  calories: real("calories").notNull().default(0),
  protein: real("protein").notNull().default(0),
  carbs: real("carbs").notNull().default(0),
  fat: real("fat").notNull().default(0),
  fiber: real("fiber").notNull().default(0),
  sugar: real("sugar").notNull().default(0),
  saturatedFat: real("saturated_fat").notNull().default(0),
  transFat: real("trans_fat").notNull().default(0),
  sodium: real("sodium").notNull().default(0),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
}, (table) => [
  index("meal_diary_tenant_idx").on(table.tenantId),
  index("meal_diary_date_idx").on(table.userId, table.date),
]);

// Water intake logs
export const waterLogs = pgTable("water_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  date: text("date").notNull(),
  amountMl: real("amount_ml").notNull(),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
}, (table) => [
  index("water_logs_date_idx").on(table.userId, table.date),
]);

// Daily nutrition goals
export const nutritionGoals = pgTable("nutrition_goals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().unique().references(() => users.id, { onDelete: "cascade" }),
  calories: real("calories").notNull().default(2000),
  protein: real("protein").notNull().default(150),
  carbs: real("carbs").notNull().default(250),
  fat: real("fat").notNull().default(65),
  fiber: real("fiber").notNull().default(25),
  waterMl: real("water_ml").notNull().default(2500),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

// Exercise library
export const exercises = pgTable("exercises", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").references(() => users.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  category: text("category").notNull().default("strength"), // strength, cardio, flexibility
  muscleGroups: text("muscle_groups").array().notNull().default(sql`'{}'::text[]`),
  equipment: text("equipment").notNull().default("none"),
  instructions: text("instructions"),
  isCustom: boolean("is_custom").notNull().default(false),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
}, (table) => [
  index("exercises_tenant_idx").on(table.tenantId),
]);

// Workout sessions
export const workoutSessions = pgTable("workout_sessions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  date: text("date").notNull(),
  name: text("name").notNull(),
  notes: text("notes"),
  durationMinutes: integer("duration_minutes"),
  isTemplate: boolean("is_template").notNull().default(false),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
}, (table) => [
  index("workout_sessions_tenant_idx").on(table.tenantId),
  index("workout_sessions_date_idx").on(table.userId, table.date),
]);

// Workout sets (exercises logged per session)
export const workoutSets = pgTable("workout_sets", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  sessionId: varchar("session_id").notNull().references(() => workoutSessions.id, { onDelete: "cascade" }),
  exerciseId: varchar("exercise_id").references(() => exercises.id, { onDelete: "set null" }),
  exerciseName: text("exercise_name").notNull(),
  setNumber: integer("set_number").notNull(),
  reps: integer("reps"),
  weightLbs: real("weight_lbs"),
  durationSeconds: integer("duration_seconds"),
  distanceKm: real("distance_km"),
  isWarmup: boolean("is_warmup").notNull().default(false),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
}, (table) => [
  index("workout_sets_session_idx").on(table.sessionId),
]);

// Health measurements (weight, body measurements, vitals)
export const healthLogs = pgTable("health_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  date: text("date").notNull(),
  // Generic metric columns (used by health-metrics page for arbitrary metric types)
  metricType: text("metric_type"),
  value: real("value"),
  unit: text("unit").default(""),
  // Explicit typed columns for fitness tracking routes
  weightKg: real("weight_kg"),
  bodyFatPct: real("body_fat_pct"),
  muscleMassPct: real("muscle_mass_pct"),
  bmi: real("bmi"),
  systolicBp: real("systolic_bp"),
  diastolicBp: real("diastolic_bp"),
  restingHeartRate: real("resting_heart_rate"),
  sleepHours: real("sleep_hours"),
  stepsCount: integer("steps_count"),
  // Additional typed columns for health-metrics page entries
  bloodSugar: real("blood_sugar"),
  caloriesBurned: real("calories_burned"),
  heightCm: real("height_cm"),
  chestCm: real("chest_cm"),
  waistCm: real("waist_cm"),
  hipsCm: real("hips_cm"),
  armsCm: real("arms_cm"),
  thighsCm: real("thighs_cm"),
  notes: text("notes"),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
}, (table) => [
  index("health_logs_tenant_idx").on(table.tenantId),
  index("health_logs_user_date_idx").on(table.userId, table.date),
]);

// Insert schemas & types
export const insertFoodItemSchema = zodInsertSchema(foodItems).omit({ id: true, createdAt: true });
export type FoodItem = typeof foodItems.$inferSelect;
export type InsertFoodItem = z.infer<typeof insertFoodItemSchema>;

export const insertMealDiaryEntrySchema = zodInsertSchema(mealDiaryEntries).omit({ id: true, createdAt: true });
export type MealDiaryEntry = typeof mealDiaryEntries.$inferSelect;
export type InsertMealDiaryEntry = z.infer<typeof insertMealDiaryEntrySchema>;

export const insertWaterLogSchema = zodInsertSchema(waterLogs).omit({ id: true, createdAt: true });
export type WaterLog = typeof waterLogs.$inferSelect;
export type InsertWaterLog = z.infer<typeof insertWaterLogSchema>;

export const insertNutritionGoalsSchema = zodInsertSchema(nutritionGoals).omit({ id: true, updatedAt: true });
export type NutritionGoals = typeof nutritionGoals.$inferSelect;
export type InsertNutritionGoals = z.infer<typeof insertNutritionGoalsSchema>;

export const insertExerciseSchema = zodInsertSchema(exercises).omit({ id: true, createdAt: true });
export type Exercise = typeof exercises.$inferSelect;
export type InsertExercise = z.infer<typeof insertExerciseSchema>;

export const insertWorkoutSessionSchema = zodInsertSchema(workoutSessions).omit({ id: true, createdAt: true });
export type WorkoutSession = typeof workoutSessions.$inferSelect;
export type InsertWorkoutSession = z.infer<typeof insertWorkoutSessionSchema>;

export const insertWorkoutSetSchema = zodInsertSchema(workoutSets).omit({ id: true, createdAt: true });
export type WorkoutSet = typeof workoutSets.$inferSelect;
export type InsertWorkoutSet = z.infer<typeof insertWorkoutSetSchema>;

export const insertHealthLogSchema = zodInsertSchema(healthLogs).omit({ id: true, createdAt: true });
export type HealthLog = typeof healthLogs.$inferSelect;
export type InsertHealthLog = z.infer<typeof insertHealthLogSchema>;

// ==================== TRACKER X ====================
// Vision boards — user-curated image boards (carousel or collage) built from
// media library files, Google Drive images, or images attached to goals.
export type VisionBoardImage = {
  id: string; // client-generated key for ordering
  source: "media" | "goal" | "drive";
  mediaFileId?: string; // for source media/goal — rendered via /api/media/:id/content
  goalId?: string; // when picked from a goal
  driveFileId?: string; // for source drive
  url?: string; // for drive images (thumbnailLink)
  name?: string;
};

export const visionBoards = pgTable("vision_boards", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  displayMode: text("display_mode").notNull().default("carousel"), // 'carousel' | 'collage'
  slideIntervalSeconds: integer("slide_interval_seconds").notNull().default(5),
  images: jsonb("images").$type<VisionBoardImage[]>().notNull().default(sql`'[]'::jsonb`),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

export type VisionBoard = typeof visionBoards.$inferSelect;

export const trackerX = pgTable("tracker_x", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  category: text("category").notNull().default("General"),
  valueType: text("value_type").notNull().default("number"), // 'number' | 'weight' | 'currency'
  unitLabel: text("unit_label"), // e.g. 'kg', 'lbs', '$', 'reps', 'mins'
  period: text("period").notNull().default("daily"), // 'daily' | 'weekly' | 'monthly'
  goalValue: doublePrecision("goal_value"),
  timeSlots: jsonb("time_slots").$type<string[]>(), // e.g. ['Morning', 'Afternoon', 'Evening']
  goalId: varchar("goal_id").references(() => goals.id, { onDelete: "set null" }),
  habitId: varchar("habit_id").references(() => habits.id, { onDelete: "set null" }),
  sortOrder: integer("sort_order").notNull().default(0),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

export const trackerXEntries = pgTable("tracker_x_entries", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  trackerXId: varchar("tracker_x_id").notNull().references(() => trackerX.id, { onDelete: "cascade" }),
  tenantId: varchar("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  date: timestamp("date").notNull(),
  timeSlot: text("time_slot"),
  value: doublePrecision("value").notNull(),
  notes: text("notes"),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

export type TrackerX = typeof trackerX.$inferSelect;
export type TrackerXEntry = typeof trackerXEntries.$inferSelect;

// ── Backup scheduler heartbeats ───────────────────────────────────────────────
// One row per named scheduled job; records when the job last ran so that
// autoscale instances can detect missed runs across idle gaps. Declaring it
// here (mirroring lib/db) prevents the Publish diff from treating it as an
// "extra" production table and offering to DROP it.
export const schedulerHeartbeats = pgTable("scheduler_heartbeats", {
  jobName: text("job_name").primaryKey(),
  lastCheckAt: timestamp("last_check_at").notNull().default(sql`now()`),
});

export type SchedulerHeartbeat = typeof schedulerHeartbeats.$inferSelect;
